import { useState, useEffect, useRef, useCallback, useLayoutEffect, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePriceMatrix, type PriceVerdict } from '../hooks/usePriceMatrix'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { useAuthFetch } from '../lib/useAuthFetch'
import { classifySku, summarize, inQueue, MARKET_EPSILON, type ReviewClass } from '../lib/reviewQueue'
import { OperatorTypeFilter, type OperatorType } from '../components/filters/OperatorTypeFilter'
import {
  Card,
  FilterBar,
  PageHeader,
  SectionLabel,
  Pill,
  StatTile,
  DataTable,
  DetailPanel,
  Sparkline,
  type ActiveFilterChip,
  type Column,
  type PillTone,
} from '../components/ui'
import { MemberPicker, NotifySelect } from '../components/shared/AssignControls'
import { useEligibleAssignees } from '../hooks/useEligibleAssignees'
import { validateAssignForm, type NotifyOption } from '../lib/taskAssign'

const API = import.meta.env.VITE_API_URL ?? ''

interface Location { id: string; name: string }

type View = 'priority' | 'byrival' | 'matrix'
// Price position = your price vs the market average (renamed from the old "Gap"
// filter). Independent of rival count — a pure position, not a review class.
type PricePosition = 'all' | 'above' | 'below' | 'at' | 'notcarried'
type PromoFilter = 'all' | 'promo' | 'nopromo'
// Recommended order is the default deterministic queue sort (see queueCompare).
type SortMode = 'recommended' | 'gap' | 'pct' | 'rivals' | 'promo' | 'brand'
// By-rival sort keys (its own toolbar; default = most pricing pressure).
type RivalSortMode = 'pressure' | 'shared' | 'avgDiff' | 'maxDiff' | 'higher' | 'name'

// Brand is real (menu_items.brand, ~77% populated). NULL/'' rows bucket here —
// never faked, never name-parsed.
const UNBRANDED = '__unbranded__'
const UNBRANDED_LABEL = 'Unbranded / other'

// A first screen you can finish reading. Both of these lists are ranked — the
// queue by urgency, the rivals by pressure — so the top of the list is where
// the decisions are, and an unbounded scroll mostly communicates "this is
// endless". The rest is one click away, never hidden.
const QUEUE_PAGE = 10
const RIVAL_PAGE = 10

// Short forms for the active-filter chips. The dropdowns carry the long,
// explanatory option labels; a chip has ~190px and only has to identify.
const QUEUE_SORT_LABEL: Record<SortMode, string> = {
  recommended: 'Recommended',
  gap: 'Dollar gap',
  pct: 'Percentage gap',
  rivals: 'Matched rivals',
  promo: 'Promotion pressure',
  brand: 'Brand',
}
const RIVAL_SORT_LABEL: Record<RivalSortMode, string> = {
  pressure: 'Pricing pressure',
  shared: 'Shared SKUs',
  avgDiff: 'Average difference',
  maxDiff: 'Single difference',
  higher: "Where you're higher",
  name: 'Rival name',
}

interface CellData {
  price: number
  inStock: boolean
  onPromo: boolean
  lastUpdated: string
  gap: number | null        // your price − this rival's price (positive = you're higher)
  verdict: PriceVerdict     // your standing vs THIS rival
}

interface DrawerData {
  product: string
  competitor: string
  theirPrice: number
  yourPrice: number | null
  allPrices: { name: string; price: number }[]
  onPromo: boolean
}

// Matrix cell popover (round 2 §13). Anchored at the clicked cell's viewport
// coordinates; a small contextual card, NOT the full-screen drawer.
interface CellPopData {
  product: string
  competitor: string
  competitorId: string | null
  theirPrice: number
  yourPrice: number | null
  onPromo: boolean
  // Anchor geometry of the clicked cell (NOT the mouse point). Anchoring to the
  // pointer put the card's top edge inside the 30px row, so the popover covered
  // the bottom half of the very row it was describing.
  x: number      // cell left
  y: number      // cell bottom — the card hangs below the row
  yTop: number   // cell top — used to flip the card above the row near the fold
}

// One SKU row in the Needs-review table. All fields are derived from the same
// matrix data — `reviewClass` (from lib/reviewQueue) drives status colour and
// the recommended sort; nothing here is a new API field.
interface QueueRow {
  product: string
  brand: string | null
  category: string | null
  rivals: number
  yourPrice: number | null
  marketAvg: number
  rivalLow: number
  gapDollar: number | null   // your price − market avg (positive = you're higher)
  gapPct: number | null
  reviewClass: ReviewClass
  promoActive: boolean
  promoRivals: number        // # matched rivals currently on promo
  firstRival: { name: string; price: number } | null
}

// ── Saved review decisions (price_review_state, migration 027) ───────────────

type ReviewState = 'open' | 'reviewed' | 'snoozed' | 'not_comparable'

interface ReviewStateRow {
  id: string
  product_key: string
  product_name: string
  state: ReviewState
  snoozed_until: string | null
  updated_by: string | null
  updated_at: string
  open_task_count?: string | number
}

// Mirrors productKey() in packages/api/src/routes/pricing.ts. Read-only on the
// client: writes always send the display name and let the server key it.
function reviewKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ')
}

// A decision that takes the SKU out of the queue. 'open' (and an expired snooze,
// which the API already reports as 'open') leaves it in.
const HANDLED_STATES = new Set<ReviewState>(['reviewed', 'snoozed', 'not_comparable'])

const STATE_LABEL: Record<Exclude<ReviewState, 'open'>, string> = {
  reviewed: 'Reviewed',
  snoozed: 'Snoozed',
  not_comparable: 'Not comparable',
}

const SNOOZE_DAYS = 30

// Parity band: prices within $0.50 either way count as "at parity". Used only by
// the by-rival / matrix cell verdicts (from the API); the review-queue
// classification uses lib/reviewQueue's tighter MARKET_EPSILON instead.
const PARITY = 0.5

// Your standing on a SKU vs a single reference price (a rival, or the rival average).
function youVerdict(yourPrice: number | null, ref: number): PriceVerdict {
  if (yourPrice == null) return 'no_own_price'
  if (yourPrice > ref + PARITY) return 'higher'
  if (yourPrice < ref - PARITY) return 'cheaper'
  return 'parity'
}

// Signed currency string (you-vs-them gap). "−" is the figure-dash to match the
// rest of the screen; positive keeps the leading "+".
function signedMoney(n: number) {
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}$${Math.abs(n).toFixed(2)}`
}

// Plain-English direction of a you-minus-them gap (round 2 §5/§13). Positive =
// you're higher (exposed); negative = you're lower. The sign alone was
// ambiguous at competitor level, so these spell out whose side the difference
// falls on. Used by the matrix cell popover, the drawer, and the By-rival table.
function diffPhrase(gap: number): string {
  if (gap > PARITY) return `You are $${Math.abs(gap).toFixed(2)} higher`
  if (gap < -PARITY) return `You are $${Math.abs(gap).toFixed(2)} lower`
  return 'Within $0.50 (parity)'
}
// A mean of signed gaps, so opposing differences cancel: +$10, −$10 and +$1
// average to +$0.33 on a rival you are badly out of step with. The metric is
// still worth showing — it answers "am I broadly dearer or cheaper than this
// rival" — but it is named "net" and carries this definition everywhere it
// appears so nobody reads it as typical SKU-level distance.
const AVG_DIFF_HELP =
  "Average of your price minus the rival's price across comparable products. " +
  'Positive means your average price is higher. Opposing differences cancel out, ' +
  'so a near-zero net average can still hide large per-product gaps.'

// Compact form for the By-rival "Largest difference" cell: "You higher $5.00" /
// "You lower $3.00" — directional, never sign-only.
function diffShort(gap: number): string {
  if (gap > PARITY) return `You higher $${Math.abs(gap).toFixed(2)}`
  if (gap < -PARITY) return `You lower $${Math.abs(gap).toFixed(2)}`
  return 'even'
}

// Colour a you-minus-them gap by status only: you priced higher = danger,
// you priced lower = positive, within the parity band = neutral text-3.
function gapColor(gap: number | null): string {
  if (gap == null) return 'var(--text-3)'
  if (gap > PARITY) return 'var(--danger)'
  if (gap < -PARITY) return 'var(--positive)'
  return 'var(--text-3)'
}

// ── Review-class presentation — status colour + label, defined once ────────────
// Colour carries status only. volumeRisk = you're exposed (danger); margin =
// warm/warning; limited evidence + catalog gap = quiet text-3; competitive =
// positive (never appears in the queue but defined for completeness).
const CLASS_COLOR: Record<ReviewClass, string> = {
  volumeRisk: 'var(--danger)',
  marginOpportunity: 'var(--warm)',
  limitedEvidence: 'var(--text-3)',
  competitive: 'var(--positive)',
  catalogGap: 'var(--text-3)',
}
// Say what the row IS, not what process it belongs to. "Margin review" told the
// operator nothing; "Below market" tells them their price is under the market
// average, which is the whole finding. Longer explanation rides in the tooltip.
const CLASS_STATUS_LABEL: Record<ReviewClass, string> = {
  volumeRisk: 'Above market',
  marginOpportunity: 'Below market',
  limitedEvidence: 'Few rivals',
  competitive: 'At market',
  catalogGap: "Don't carry",
}

const CLASS_STATUS_HELP: Record<ReviewClass, string> = {
  volumeRisk: 'Your price is above the market average. You may be losing volume to cheaper rivals.',
  marginOpportunity: 'Your price is below the market average. You may be leaving margin on the table.',
  limitedEvidence: 'Only one rival carries this, so the market average is thin evidence. Treat with caution.',
  competitive: 'Your price is level with the market average.',
  catalogGap: "Rivals carry this and you don't.",
}
const CLASS_PILL_TONE: Record<ReviewClass, PillTone> = {
  volumeRisk: 'danger',
  marginOpportunity: 'warning',
  limitedEvidence: 'neutral',
  competitive: 'accent',
  catalogGap: 'neutral',
}
// Deterministic queue precedence: volumeRisk before marginOpportunity before
// limitedEvidence (competitive / catalogGap sort last; they never enter the queue).
const CLASS_RANK: Record<ReviewClass, number> = {
  volumeRisk: 0,
  marginOpportunity: 1,
  limitedEvidence: 2,
  competitive: 3,
  catalogGap: 4,
}

// you-vs-rival difference colour: you higher = danger, you lower = accent, within
// the parity band = quiet. Distinct from gapColor's --positive — the s07 By-rival
// / matrix work pins "lower" to --accent, and never uses the word "cheaper".
function diffColor(gap: number | null): string {
  if (gap == null) return 'var(--text-3)'
  if (gap > PARITY) return 'var(--danger)'
  if (gap < -PARITY) return 'var(--accent)'
  return 'var(--text-3)'
}

// One shared SKU inside a rival's profile. gap = your price − their price
// (positive = you're higher than this rival). Only SKUs you both carry appear,
// so yourPrice is always present.
interface RivalSku {
  product: string
  category: string | null
  brand: string | null
  yourPrice: number
  theirPrice: number
  gap: number
  onPromo: boolean
}

// Competitive position label — order matters (see rivalPositionOf).
type RivalPosition = 'pressure' | 'lower' | 'aligned' | 'mixed' | 'limited'

interface RivalCatPressure {
  category: string
  inCat: number        // shared SKUs in this category
  higherInCat: number  // of those, how many you price above the rival
}

// Everything the By-rival table + profile drawer + matrix headers need about one
// rival, computed once over its shared SKUs (SKUs you both carry).
interface RivalStat {
  competitor: string
  businessType: 'storefront' | 'delivery' | null
  shared: number
  higher: number       // gap > PARITY
  lower: number        // gap < −PARITY
  parity: number       // |gap| ≤ PARITY
  avgDiff: number | null   // mean(gap), signed
  maxDiff: number | null   // the gap with the largest |value|, signed
  position: RivalPosition
  sentence: string
  pressure: number     // deterministic "most pricing pressure" sort key
  skus: RivalSku[]
  categoryPressure: RivalCatPressure[]
}

const RIVAL_POSITION_LABEL: Record<RivalPosition, string> = {
  pressure: 'Pricing pressure',
  lower: 'You generally lower',
  aligned: 'Mostly aligned',
  mixed: 'Mixed',
  limited: 'Not enough data',
}
const RIVAL_POSITION_TONE: Record<RivalPosition, PillTone> = {
  pressure: 'danger',
  lower: 'accent',
  aligned: 'neutral',
  mixed: 'warning',
  limited: 'neutral',
}

// First match wins — evaluate in this exact order (s07 brief, deterministic).
function rivalPositionOf(shared: number, higher: number, lower: number, parity: number): RivalPosition {
  if (shared < 5) return 'limited'
  if (higher / shared >= 0.5) return 'pressure'
  if (lower / shared >= 0.5) return 'lower'
  if (parity / shared >= 0.4) return 'aligned'
  return 'mixed'
}

// Plain, factual sentence under the label — no adjectives.
function rivalSentence(pos: RivalPosition, rival: string, shared: number, higher: number, lower: number, parity: number): string {
  const s = shared === 1 ? '' : 's'
  switch (pos) {
    case 'pressure': return `You're priced above ${rival} on ${higher} of ${shared} shared products.`
    case 'lower':    return `You're priced below ${rival} on ${lower} of ${shared} shared products.`
    case 'aligned':  return `You match ${rival} within $0.50 on ${parity} of ${shared} shared products.`
    case 'mixed':    return `Split against ${rival}: ${higher} higher, ${parity} level, ${lower} lower.`
    case 'limited':  return `Only ${shared} shared product${s} — too little overlap to read a position.`
  }
}

// Deterministic "most pricing pressure" score — no ties on floats.
function rivalPressure(shared: number, higher: number, avgDiff: number | null): number {
  return (higher / Math.max(shared, 1)) * 100 + Math.min(shared, 50) * 0.4 + Math.max(avgDiff ?? 0, 0)
}

// Compact stacked bar — higher | parity | lower as shares of shared. A second
// read of the three counts; the counts themselves stay visible as columns.
function DistributionBar({ higher, parity, lower, shared }: { higher: number; parity: number; lower: number; shared: number }) {
  const total = Math.max(shared, 1)
  const seg = (n: number, color: string) => (n > 0 ? <div style={{ width: `${(n / total) * 100}%`, background: color }} /> : null)
  return (
    <div
      data-testid="rival-dist-bar"
      role="img"
      aria-label={`${higher} higher, ${parity} at parity, ${lower} lower of ${shared} shared`}
      // Fills its column rather than sitting at a fixed 140px. A 140px bar in a
      // 320px cell left a ~180px hole between the split and the right-hand
      // money column, which is what made the middle of By-rival read as empty
      // and the right edge read as pinned (founder review 2026-08-18).
      style={{ display: 'flex', width: '100%', maxWidth: 360, minWidth: 110, height: 10, borderRadius: 3, overflow: 'hidden', background: 'var(--surface-3)' }}
    >
      {seg(higher, 'var(--danger)')}
      {seg(parity, 'var(--slate)')}
      {seg(lower, 'var(--accent)')}
    </div>
  )
}


// "you vs them" palette: you-higher = you're exposed (rival undercuts) → danger;
// you-cheaper = you lead → accent; parity = slate; not-carried = neutral surface.
// The cell background is now the primary direction signal (the delta was demoted
// to quiet grey), so the two directional tints are strengthened enough to read at
// a glance without the coloured number. Parity stays faint — it is the absence of
// a story, not a third colour competing for attention.
const VERDICT_BG: Record<PriceVerdict, string> = {
  higher: 'rgba(196,59,78,0.16)',
  cheaper: 'rgba(11,184,184,0.15)',
  parity: 'rgba(84,132,164,0.07)',
  no_own_price: 'var(--surface-3)',
}
const VERDICT_TEXT: Record<PriceVerdict, string> = {
  higher: 'var(--danger)',
  cheaper: 'var(--accent)',
  parity: 'var(--text-3)',
  no_own_price: 'var(--text-3)',
}
const VERDICT_TONE: Record<PriceVerdict, 'danger' | 'accent' | 'neutral'> = {
  higher: 'danger',
  cheaper: 'accent',
  parity: 'neutral',
  no_own_price: 'neutral',
}
const VERDICT_BADGE: Record<PriceVerdict, string> = {
  higher: "YOU'RE HIGHER",
  cheaper: "YOU'RE LOWER",
  parity: 'AT PARITY',
  no_own_price: 'NOT CARRIED',
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function exportCSV(
  products: string[],
  competitors: string[],
  productMap: Map<string, Map<string, CellData>>,
  productYourPrice: Map<string, number | null>
) {
  const header = ['Product', 'Your price', ...competitors].join(',')
  const rows = products.map((product) => {
    const yp = productYourPrice.get(product)
    const yourCell = yp != null ? `$${yp.toFixed(2)}` : '—'
    const cells = competitors.map((c) => {
      const d = productMap.get(product)?.get(c)
      return d ? `$${d.price.toFixed(2)}` : '—'
    })
    return [`"${product}"`, yourCell, ...cells].join(',')
  })
  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `price-matrix-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function timeAgo(dateStr: string) {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000)
  if (diff < 1) return 'just now'
  if (diff < 60) return `${diff}m ago`
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`
  return `${Math.floor(diff / 1440)}d ago`
}

// Only the "missing" (rival doesn't carry the SKU) color survives the you-vs-them
// reframe; live cells are colored by VERDICT_BG (your price vs the rival's).
const CELL_COLORS = {
  // Transparent, not grey. A filled "not carried" cell next to a tinted live
  // cell turned the grid into a checkerboard of two greys and a pink, and the
  // eye had to decide which shade meant something. Absence now reads as
  // absence: white ground, one em-dash. Only cells that CARRY a comparison
  // carry colour.
  missing: 'transparent',
}

// ── Review-queue intelligence ribbon — one compact status row ──────────────────
// Replaces the old three-zone "Today's pricing work" band (a ~130px header) with
// a single ~52px status row that sits between the filter toolbar and the table.
// Status only — the Sort control lives in the toolbar with the other filters.
// Kept as one <style> string so the responsive breakpoints (viewport-width,
// matching the screenshot gates), the chip hover/focus/selected affordances, and
// the single-row/no-wrap contract all live in one place. Reuses only existing
// CSS variables — no new colours.
const RIBBON_CSS = `
.ribbon {
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  min-height: 52px; max-height: 56px;
  display: flex; align-items: center; gap: 18px;
  padding: 8px 18px;
  flex-shrink: 0; box-sizing: border-box;
}

/* Left → right: count block · chips · Begin review, all on one row */
.ribbon-main { display: flex; align-items: center; gap: 14px; min-width: 0; }

.ribbon-count {
  display: flex; align-items: baseline; gap: 7px;
  padding-right: 18px; border-right: 1px solid var(--border);
  flex-shrink: 0; white-space: nowrap;
}
.ribbon-count-num { font-family: var(--mono); font-size: 22px; font-weight: 700; line-height: 1; color: var(--text-1); }
.ribbon-count.is-filtered .ribbon-count-num { font-size: 16px; }
.ribbon-count-label { font-size: 11px; font-weight: 700; color: var(--text-2); }
.ribbon-clear {
  background: none; border: none; padding: 0; margin-left: 4px; cursor: pointer;
  font-family: var(--sans); font-size: 11px; font-weight: 700; color: var(--accent);
  align-self: center;
}
.ribbon-clear:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 3px; }

.ribbon-chips { display: flex; align-items: center; gap: 8px; }
.ribbon-chip {
  display: inline-flex; align-items: center; gap: 7px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 8px; padding: 7px 9px; cursor: pointer;
  font-family: var(--sans); white-space: nowrap;
  transition: background .14s ease, border-color .14s ease;
}
.ribbon-chip:hover { background: var(--surface-2); }
.ribbon-chip:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.ribbon-chip.is-active { background: var(--chip-tint); border-color: var(--chip-color); }
.ribbon-chip-num { font-family: var(--mono); font-size: 14px; font-weight: 700; line-height: 1; color: var(--chip-color); }
.ribbon-chip-label { font-size: 10px; font-weight: 700; color: var(--text-2); }

.ribbon-begin { flex-shrink: 0; }

/* Right side — the only thing pushed to the far edge */
.ribbon-catalog { margin-left: auto; flex-shrink: 0; }
.ribbon-catlink {
  background: none; border: none; padding: 0; cursor: pointer;
  font-family: var(--sans); font-size: 11px; font-weight: 600; color: var(--accent);
  white-space: nowrap;
}
.ribbon-catlink:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 3px; }
.ribbon-catlink .tnum { font-family: var(--mono); font-weight: 700; }

/* 760–1100px: the catalog link may drop to a second line; the count block,
   chips and Begin review stay together on one row. */
@media (max-width: 1099px) {
  .ribbon { flex-wrap: wrap; max-height: none; padding: 6px 14px; gap: 12px; }
  .ribbon-count { padding-right: 12px; }
  .ribbon-count-num { font-size: 19px; }
}
/* <760px: chips stay a 3-up row; catalog link goes full width beneath. */
@media (max-width: 760px) {
  .ribbon-main { flex-wrap: wrap; row-gap: 8px; }
  .ribbon-catalog { flex-basis: 100%; margin-left: 0; }
}
`

// Batch footer for the two ranked lists. Deliberately page-local: this pass is
// /prices only and nothing else needs it yet.
function ShowMore({ shown, total, page, onMore, onAll, noun, testid, card = false }: {
  shown: number; total: number; page: number
  onMore: () => void; onAll: () => void; noun: string; testid: string
  /**
   * `card` footer: one centred, balanced composition sitting on a top border
   * inside the table card — "Show N more · count · Show all". The default
   * `spread` layout (Show-more left, count adrift, Show-all pinned right by
   * marginLeft:auto) read as three detached items flung to opposite ends of the
   * card (By-rival founder review 2026-08-18). Needs-review keeps the default.
   */
  card?: boolean
}) {
  if (shown >= total) return null
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      justifyContent: card ? 'center' : 'flex-start',
      gap: card ? 14 : 12,
      padding: card ? '13px 14px' : '12px 0 4px',
      borderTop: card ? '1px solid var(--border)' : undefined,
      flexShrink: 0,
    }}>
      <button
        type="button"
        data-testid={testid}
        onClick={onMore}
        style={{
          padding: '7px 14px', borderRadius: 'var(--r-sm)',
          border: '1.5px solid var(--border-2)', background: 'var(--surface)',
          color: 'var(--text-1)', fontSize: 12, fontWeight: 600,
          fontFamily: 'var(--sans)', cursor: 'pointer',
        }}
      >
        Show {Math.min(page, total - shown)} more
      </button>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
        {shown} of {total} {noun}
      </span>
      <button
        type="button"
        onClick={onAll}
        style={{
          marginLeft: card ? 0 : 'auto', background: 'none', border: 'none', padding: 0,
          cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11,
          fontWeight: 700, color: 'var(--accent)',
        }}
      >
        Show all {total}
      </button>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

interface DropdownProps {
  label: string
  value: string
  options: { value: string; label: string; dot?: string }[]
  onChange: (v: string) => void
  active?: boolean
  title?: string
}

function Dropdown({ label, value, options, onChange, active, title }: DropdownProps) {
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const wrapRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const selected = options.find(o => o.value === value)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const t = e.target as Node
      // The menu renders in a portal on document.body — NOT inside wrapRef —
      // so it must be excluded explicitly, otherwise this mousedown handler
      // closes the menu before an option's click lands and onChange never
      // fires (the category/view filters silently do nothing).
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function handleToggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      setMenuPos({ top: r.bottom + 5, left: r.left })
    }
    setOpen(o => !o)
  }

  const menu = open ? createPortal(
    <div ref={menuRef} data-testid="prices-filter-menu" style={{
      position: 'fixed', top: menuPos.top, left: menuPos.left,
      background: 'var(--surface)', border: '1px solid var(--border-2)',
      borderRadius: 'var(--r-sm)', boxShadow: '0 8px 32px rgba(30,60,80,0.14)',
      zIndex: 9999, minWidth: 160, padding: 5,
    }}>
      {options.map(opt => (
        <div
          key={opt.value}
          onClick={() => { onChange(opt.value); setOpen(false) }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 10px', borderRadius: 6,
            fontSize: 12, color: opt.value === value ? 'var(--accent)' : 'var(--text-1)',
            fontWeight: opt.value === value ? 700 : 400,
            cursor: 'pointer', fontFamily: 'var(--sans)',
            background: 'transparent',
            transition: 'background 0.1s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-3)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          {opt.dot && (
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.dot, flexShrink: 0 }} />
          )}
          {opt.label}
          {opt.value === value && <span style={{ marginLeft: 'auto', fontSize: 11 }}>✓</span>}
        </div>
      ))}
    </div>,
    document.body
  ) : null

  return (
    <div ref={wrapRef} style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
        {label}
      </span>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          ref={btnRef}
          data-testid={`prices-filter-${label.toLowerCase().replace(/\s+/g, '-')}`}
          title={title}
          onClick={handleToggle}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '5px 11px', borderRadius: 'var(--r-sm)',
            fontSize: 11, fontWeight: 600,
            border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
            background: active ? 'var(--accent)' : 'var(--surface)',
            color: active ? '#fff' : 'var(--text-1)',
            cursor: 'pointer', fontFamily: 'var(--sans)',
            transition: 'var(--transition)', whiteSpace: 'nowrap',
          }}
        >
          {selected?.dot && (
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: selected.dot, flexShrink: 0, display: 'inline-block' }} />
          )}
          {selected?.label || options[0].label}
          <ChevronDown />
        </button>
        {menu}
      </div>
    </div>
  )
}

// ── Matrix Rivals selector ────────────────────────────────────────────────────
// Multi-select checkbox menu (same portal + mousedown-close pattern as Dropdown).
// Lists every tracked rival with its shared count, plus Select all / Clear /
// Top 5. Horizontal scrolling is expected — this never shrinks columns to fit.
function MatrixRivalsMenu({
  rivals, selected, total, onChange, top5,
}: {
  rivals: { name: string; shared: number }[]
  selected: Set<string>
  total: number
  onChange: (next: Set<string>) => void
  top5: string[]
}) {
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const wrapRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function handleToggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      setMenuPos({ top: r.bottom + 5, left: r.left })
    }
    setOpen((o) => !o)
  }

  const toggleRival = (name: string) => {
    const next = new Set(selected)
    if (next.has(name)) next.delete(name)
    else next.add(name)
    onChange(next)
  }

  const action: CSSProperties = {
    background: 'none', border: 'none', padding: '2px 6px', cursor: 'pointer',
    fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700, color: 'var(--accent)',
  }

  const menu = open ? createPortal(
    <div ref={menuRef} data-testid="matrix-rivals-menu" style={{
      position: 'fixed', top: menuPos.top, left: menuPos.left,
      background: 'var(--surface)', border: '1px solid var(--border-2)',
      borderRadius: 'var(--r-sm)', boxShadow: '0 8px 32px rgba(30,60,80,0.14)',
      zIndex: 9999, width: 260, padding: 5,
    }}>
      <div style={{ display: 'flex', gap: 4, padding: '4px 4px 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
        <button style={action} onClick={() => onChange(new Set(rivals.map((r) => r.name)))}>Select all</button>
        <button style={{ ...action, color: 'var(--text-2)' }} onClick={() => onChange(new Set())}>Clear</button>
        <button style={action} onClick={() => onChange(new Set(top5))}>Top 5</button>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {rivals.map((r) => {
          const on = selected.has(r.name)
          return (
            <div
              key={r.name}
              onClick={() => toggleRival(r.name)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6,
                cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--text-1)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-3)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{
                width: 14, height: 14, borderRadius: 4, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border-2)'}`,
                background: on ? 'var(--accent)' : 'transparent',
              }}>
                {on && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><polyline points="20 6 9 17 4 12" /></svg>}
              </span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name}>{r.name}</span>
              <span className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }}>{r.shared}</span>
            </div>
          )
        })}
      </div>
    </div>,
    document.body,
  ) : null

  return (
    <div ref={wrapRef} style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Rivals</span>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          ref={btnRef}
          data-testid="matrix-rivals-button"
          onClick={handleToggle}
          style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 'var(--r-sm)',
            fontSize: 11, fontWeight: 600, border: '1.5px solid var(--border-2)',
            background: 'var(--surface)', color: 'var(--text-1)', cursor: 'pointer',
            fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
          }}
        >
          {`${selected.size}/${total}`}
          <ChevronDown />
        </button>
        {menu}
      </div>
    </div>
  )
}

// ── Rival Pricing Profile drawer ──────────────────────────────────────────────

// A small labelled bar row used by both PRICING POSITION and CATEGORY PRESSURE.
function ProfileBar({ label, count, right, pct, color }: { label: string; count: number; right?: string; pct: number; color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 78, flexShrink: 0, fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>{label}</span>
      <span className="tnum" style={{ width: 24, flexShrink: 0, textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-1)' }}>{count}</span>
      <div style={{ flex: 1, height: 8, background: 'var(--surface-3)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span className="tnum" style={{ width: 40, flexShrink: 0, textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>{right}</span>
    </div>
  )
}

// Slide-out Rival Pricing Profile, opened from a By-rival table row. Explains the
// pricing relationship (position split + category pressure) BEFORE the evidence
// (the shared-SKU table). Operator type only — no rival location is available.
function RivalDrawer({ data, open, onClose, onAssign, openTasksFor }: {
  data: RivalStat | null
  open: boolean
  onClose: () => void
  /** Dispatch this specific price difference to a teammate. */
  onAssign: (sku: { product: string; brand: string | null; category: string | null; yourPrice: number; theirPrice: number; gap: number; onPromo: boolean }) => void
  openTasksFor: (product: string) => number
}) {
  const [dCategory, setDCategory] = useState('')
  const [dBrand, setDBrand] = useState('')
  const [dPos, setDPos] = useState<PricePosition>('all')
  const [dPromo, setDPromo] = useState<PromoFilter>('all')

  // Reset the drawer filters whenever a different rival is opened.
  const competitor = data?.competitor ?? null
  useEffect(() => { setDCategory(''); setDBrand(''); setDPos('all'); setDPromo('all') }, [competitor])

  if (!data) return null

  const opLabel = data.businessType === 'storefront' ? 'Storefront' : data.businessType === 'delivery' ? 'Delivery' : 'Operator type unknown'
  const shared = data.shared

  // Position split percentages — rounded, guaranteed to sum to 100 (the largest
  // bucket absorbs the rounding remainder).
  const buckets = [data.higher, data.parity, data.lower]
  const pcts = buckets.map((n) => (shared ? Math.round((n / shared) * 100) : 0))
  const rem = 100 - pcts.reduce((a, b) => a + b, 0)
  if (shared > 0 && rem !== 0) {
    const maxIdx = buckets.indexOf(Math.max(...buckets))
    pcts[maxIdx] += rem
  }

  // Drawer filter option lists, derived from this rival's shared SKUs.
  const catOptions = [...new Set(data.skus.map((s) => s.category).filter((c): c is string => !!c))].sort((a, b) => a.localeCompare(b))
  const brandNames = [...new Set(data.skus.map((s) => s.brand).filter((b): b is string => !!b))].sort((a, b) => a.localeCompare(b))
  const hasUnbranded = data.skus.some((s) => s.brand == null)

  const filteredSkus = data.skus.filter((s) => {
    if (dCategory && (s.category || '') !== dCategory) return false
    if (dBrand === UNBRANDED ? s.brand != null : dBrand && s.brand !== dBrand) return false
    if (dPos === 'above' && !(s.gap > PARITY)) return false
    if (dPos === 'below' && !(s.gap < -PARITY)) return false
    if (dPos === 'at' && !(Math.abs(s.gap) <= PARITY)) return false
    if (dPromo === 'promo' && !s.onPromo) return false
    if (dPromo === 'nopromo' && s.onPromo) return false
    return true
  })
  const maxCatHigher = data.categoryPressure.length ? Math.max(1, ...data.categoryPressure.map((c) => c.higherInCat)) : 1

  return (
    <div data-testid="rival-drawer" style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 460,
      background: 'var(--surface-solid)', borderLeft: '1px solid var(--border-2)',
      boxShadow: '-12px 0 40px rgba(0,0,0,0.18), -2px 0 8px rgba(0,0,0,0.06)',
      zIndex: 50, display: 'flex', flexDirection: 'column',
      transform: open ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s cubic-bezier(.2,.8,.2,1)',
    }}>
      {/* Header — name, eyebrow, operator type (no location) */}
      <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <button
          onClick={onClose}
          style={{
            width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-3)',
            border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-2)', flexShrink: 0,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div data-testid="rival-drawer-title" style={{ flex: 1, minWidth: 0 }}>
          <DetailPanel
            eyebrow={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
              {`TRACKING · ${shared} SHARED SKUS`}
            </span>}
            title={data.competitor}
            style={{ marginBottom: 0 }}
          />
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.04em', marginTop: 4 }}>{opLabel}</div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '14px 16px 16px' }}>
        {/* 1 · Pricing relationship — three signed stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 7, marginBottom: 14, flexShrink: 0 }}>
          <StatTile size="sm" value={shared} label="Shared SKUs" />
          {/* "Net" is load-bearing: opposing per-SKU differences cancel here, so
              +$10 / −$10 / +$1 averages to near zero on a rival you are wildly
              out of step with. The tooltip states the formula. */}
          <StatTile size="sm" tone={data.avgDiff == null ? 'default' : data.avgDiff > PARITY ? 'danger' : data.avgDiff < -PARITY ? 'accent' : 'default'}
            value={data.avgDiff == null ? '—' : signedMoney(data.avgDiff)} label="Net avg. difference"
            title={AVG_DIFF_HELP} />
          {/* The largest difference is stated once, in words, in the callout
              below — so this third tile carries the share of comparable SKUs
              where you are the higher-priced one instead of repeating it. */}
          <StatTile size="sm" tone={data.higher > 0 ? 'danger' : 'default'}
            value={`${data.higher} / ${shared}`} label="You're higher"
            title={`You are the higher-priced operator on ${data.higher} of ${shared} comparable product${shared === 1 ? '' : 's'}.`} />
        </div>

        {/* Largest difference in plain English — the sign alone is ambiguous, so
            spell out the direction and reframe it as exposure when you're the
            higher-priced one. */}
        {data.maxDiff != null && Math.abs(data.maxDiff) > PARITY && (
          <div style={{ marginBottom: 14, padding: '9px 11px', background: 'var(--surface-3)', border: '1px solid var(--border)', borderLeft: `3px solid ${data.maxDiff > 0 ? 'var(--danger)' : 'var(--accent)'}`, borderRadius: 'var(--r-sm)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 3 }}>
              {data.maxDiff > 0 ? 'Largest exposure' : 'Largest price difference'}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: data.maxDiff > 0 ? 'var(--danger)' : 'var(--accent)' }}>{diffPhrase(data.maxDiff)}</div>
          </div>
        )}

        {/* 2 · Pricing position split */}
        <SectionLabel style={{ marginBottom: 8 }}>Pricing position</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
          <ProfileBar label="You higher" count={data.higher} pct={pcts[0]} right={`${pcts[0]}%`} color="var(--danger)" />
          <ProfileBar label="At parity" count={data.parity} pct={pcts[1]} right={`${pcts[1]}%`} color="var(--slate)" />
          <ProfileBar label="You lower" count={data.lower} pct={pcts[2]} right={`${pcts[2]}%`} color="var(--accent)" />
        </div>

        {/* 4 · Largest differences (evidence) — filters, then the shared-SKU table */}
        <SectionLabel style={{ marginBottom: 8 }}>Largest differences</SectionLabel>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Dropdown label="Category" value={dCategory} onChange={setDCategory} active={!!dCategory}
            options={[{ value: '', label: 'All categories' }, ...catOptions.map((c) => ({ value: c, label: c }))]} />
          <Dropdown label="Brand" value={dBrand} onChange={setDBrand} active={!!dBrand}
            options={[
              { value: '', label: 'All brands' },
              ...(hasUnbranded ? [{ value: UNBRANDED, label: UNBRANDED_LABEL, dot: 'var(--text-3)' }] : []),
              ...brandNames.map((b) => ({ value: b, label: b })),
            ]} />
          <Dropdown label="Price position" value={dPos} onChange={(v) => setDPos(v as PricePosition)} active={dPos !== 'all'}
            options={[
              { value: 'all', label: 'All positions' },
              { value: 'above', label: 'You higher', dot: 'var(--danger)' },
              { value: 'at', label: 'At parity', dot: 'var(--slate)' },
              { value: 'below', label: 'You lower', dot: 'var(--accent)' },
            ]} />
          <Dropdown label="Promotion" value={dPromo} onChange={(v) => setDPromo(v as PromoFilter)} active={dPromo !== 'all'}
            options={[
              { value: 'all', label: 'All' },
              { value: 'promo', label: 'On promo', dot: 'var(--warm)' },
              { value: 'nopromo', label: 'No promo', dot: 'var(--slate)' },
            ]} />
        </div>
        {/* Stacked comparison list (round 2 §6) — the drawer is too narrow for a
            six-column table (the headers overlapped and Promo clipped). Each SKU
            is a card: name/brand/category, a YOU-vs-RIVAL price pair, and the
            directional difference. Sorted by largest actionable difference (the
            skus are pre-sorted by |gap| desc). */}
        <div style={{ marginBottom: 16 }}>
          {filteredSkus.length === 0 ? (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', fontFamily: 'var(--mono)', padding: '8px 0' }}>No shared products match these filters.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filteredSkus.map((s) => (
                <div key={s.product} data-testid="rival-sku-row" style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div title={s.product} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.product}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)', marginTop: 2, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[s.brand, s.category].filter(Boolean).join(' · ') || '—'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.1em', color: 'var(--text-3)', marginBottom: 1 }}>YOU</div>
                      <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>${s.yourPrice.toFixed(2)}</div>
                    </div>
                    <div style={{ textAlign: 'right', minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.1em', color: 'var(--text-3)', marginBottom: 1 }}>RIVAL</div>
                      <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color: 'var(--text-2)' }}>${s.theirPrice.toFixed(2)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: diffColor(s.gap) }}>{diffPhrase(s.gap)}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {s.onPromo && <Pill variant="chip" tone="blocked-by">SALE</Pill>}
                      {/* Every other path in this screen ended here — you could
                          see a $18.85 exposure and do nothing about it. Assign
                          hands the specific difference to whoever edits the
                          menu, pre-worded from the numbers on this card. */}
                      {(() => {
                        const openTasks = openTasksFor(s.product)
                        return (
                          <button
                            type="button"
                            data-testid="rival-sku-assign"
                            title={openTasks > 0
                              ? `${openTasks} open task${openTasks === 1 ? '' : 's'} on this product. Click to add another.`
                              : `Assign this price difference to a teammate.`}
                            onClick={(e) => { e.stopPropagation(); onAssign(s) }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 9px', borderRadius: 'var(--r-sm)', cursor: 'pointer',
                              fontFamily: 'var(--sans)', fontSize: 10.5, fontWeight: 700,
                              border: `1.5px solid var(--accent)`,
                              background: openTasks > 0 ? 'var(--accent)' : 'transparent',
                              color: openTasks > 0 ? '#fff' : 'var(--accent)',
                              transition: 'var(--transition)',
                            }}
                          >
                            {openTasks > 0 ? `Assigned · ${openTasks}` : 'Assign'}
                          </button>
                        )
                      })()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 5 · Where you're the higher-priced one, by category. "Category
            pressure" named a feeling; the bars actually count shared products
            in each category where your price is above this rival's. */}
        <SectionLabel style={{ marginBottom: 8 }}>Higher-priced products by category</SectionLabel>
        {data.categoryPressure.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>No shared categories.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {data.categoryPressure.map((c) => (
              <ProfileBar key={c.category} label={c.category} count={c.higherInCat} right={`of ${c.inCat}`}
                pct={(c.higherInCat / maxCatHigher) * 100} color="var(--danger)" />
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        <button
          className="btn"
          data-testid="rival-drawer-dismiss"
          style={{ width: '100%', justifyContent: 'center' }}
          onClick={onClose}
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

// ── Price history strip ───────────────────────────────────────────────────────

// Honest, real-but-sparse price history. Reuses GET /prices/history-by-product
// (menu_items-backed). History is only a handful of days old (cadence is monthly),
// so we label exactly how many days exist rather than implying a full trend.
function PriceHistoryStrip({ locationId, productName }: { locationId: string | null; productName: string }) {
  const authFetch = useAuthFetch()
  const [points, setPoints] = useState<{ date: string; price: number }[] | null>(null)

  useEffect(() => {
    if (!locationId || !productName) return
    let alive = true
    setPoints(null)
    const params = new URLSearchParams({ location_id: locationId, product_name: productName, days: '90' })
    authFetch(`${API}/api/v1/prices/history-by-product?${params}`)
      .then(r => r.json())
      .then(d => {
        if (!alive) return
        const series: Record<string, { date: string; price: number }[]> = d.data?.series || {}
        const byDate = new Map<string, number[]>()
        for (const pts of Object.values(series)) {
          for (const p of pts) {
            if (!byDate.has(p.date)) byDate.set(p.date, [])
            byDate.get(p.date)!.push(p.price)
          }
        }
        const merged = [...byDate.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([date, arr]) => ({ date, price: arr.reduce((s, x) => s + x, 0) / arr.length }))
        setPoints(merged)
      })
      .catch(() => alive && setPoints([]))
    return () => { alive = false }
  }, [locationId, productName, authFetch])

  const dayCount = points?.length ?? 0
  const label = points == null
    ? 'Loading history…'
    : dayCount <= 1
      ? `Building history — ${dayCount} day so far`
      : `Building history — ${dayCount} days so far`

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warm)', flexShrink: 0 }} />
        <SectionLabel style={{ fontWeight: 500, letterSpacing: '0.12em' }}>{label}</SectionLabel>
      </div>
      <div style={{ background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '8px 10px' }}>
        {points && points.length > 1 ? (
          <Sparkline
            points={points.map(p => p.price)}
            color="var(--accent)"
            area
            endDot
            width={300}
            height={44}
            style={{ width: '100%', height: 44, display: 'block' }}
          />
        ) : (
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', textAlign: 'center', padding: '10px 0' }}>
            {points == null ? '…' : 'Not enough history yet — one data point so far.'}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Matrix cell popover (round 2 §13) ────────────────────────────────────────
// A small contextual card anchored near the clicked competitor cell. Dismisses
// on outside click / Escape, stays inside the viewport, and is keyboard
// accessible. NOT a full-screen drawer — the matrix stays visible behind it.
function MatrixCellPopover({
  data, openTasks, onClose, onViewRival, onViewHistory, onAssign,
}: {
  data: CellPopData
  /** Open tasks already on this product, so the button can show its state. */
  openTasks: number
  onClose: () => void
  onViewRival: (competitorId: string) => void
  onViewHistory: () => void
  onAssign: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const firstBtnRef = useRef<HTMLButtonElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: data.x, top: data.y + 8 })

  // Hang the card off the BOTTOM of the clicked cell so the row it describes is
  // never covered, then clamp into the viewport. Near the fold, flip it above
  // the row rather than sliding it up over the row.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const pad = 8
    let left = data.x
    let top = data.y + 8
    if (left + r.width > window.innerWidth - pad) left = Math.max(pad, window.innerWidth - r.width - pad)
    if (top + r.height > window.innerHeight - pad) {
      const above = data.yTop - 8 - r.height
      top = above >= pad ? above : Math.max(pad, window.innerHeight - r.height - pad)
    }
    setPos({ left, top })
  }, [data.x, data.y, data.yTop, data.product, data.competitor])

  useEffect(() => {
    firstBtnRef.current?.focus()
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  const gap = data.yourPrice != null ? data.yourPrice - data.theirPrice : null
  const row = (label: string, node: React.ReactNode) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
      {node}
    </div>
  )

  return createPortal(
    <>
      <div data-testid="cell-pop-scrim" onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'transparent' }} />
      <div
        ref={ref}
        data-testid="matrix-cell-popover"
        role="dialog"
        aria-label={`Price comparison for ${data.product} versus ${data.competitor}`}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', left: pos.left, top: pos.top, zIndex: 61, width: 244,
          background: 'var(--surface-solid)', border: '1px solid var(--border-2)',
          borderRadius: 'var(--r-sm)', boxShadow: '0 12px 40px rgba(0,0,0,0.28)', padding: 12,
        }}
      >
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-1)', lineHeight: 1.3, marginBottom: 10 }}>{data.product}</div>

        {row('Your price', <span className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>{data.yourPrice != null ? `$${data.yourPrice.toFixed(2)}` : '—'}</span>)}
        {row(data.competitor, <span className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700, color: 'var(--text-2)' }}>${data.theirPrice.toFixed(2)}</span>)}
        {row('Difference', <span style={{ fontSize: 12, fontWeight: 700, color: gap == null ? 'var(--text-3)' : diffColor(gap) }}>{gap == null ? 'You don’t carry this' : diffPhrase(gap)}</span>)}
        {data.onPromo && row('Rival promotion', <Pill variant="chip" tone="blocked-by">SALE</Pill>)}

        {/* Assign is the primary action and takes its own row: the other two
            are investigations, this is the one that changes something. Three
            buttons abreast in a 244px card would each be ~75px — unreadable. */}
        <button
          ref={firstBtnRef}
          data-testid="cell-pop-assign"
          onClick={onAssign}
          title={openTasks > 0
            ? `${openTasks} open task${openTasks === 1 ? '' : 's'} on this product. Click to add another.`
            : 'Assign this price difference to a teammate.'}
          style={{
            width: '100%', marginTop: 12, padding: '7px 8px', borderRadius: 'var(--r-sm)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, cursor: 'pointer',
            fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
            border: '1.5px solid var(--accent)',
            background: openTasks > 0 ? 'var(--accent)' : 'transparent',
            color: openTasks > 0 ? '#fff' : 'var(--accent)',
            transition: 'var(--transition)',
          }}
        >
          {openTasks > 0 ? `Assigned · ${openTasks}` : 'Assign a task'}
        </button>

        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button
            className="btn"
            data-testid="cell-pop-view-rival"
            disabled={!data.competitorId}
            onClick={() => data.competitorId && onViewRival(data.competitorId)}
            style={{ flex: 1, justifyContent: 'center', fontSize: 11, padding: '6px 8px', opacity: data.competitorId ? 1 : 0.5 }}
          >
            View rival
          </button>
          <button
            className="btn"
            data-testid="cell-pop-view-history"
            onClick={onViewHistory}
            style={{ flex: 1, justifyContent: 'center', fontSize: 11, padding: '6px 8px' }}
          >
            Price history
          </button>
        </div>
      </div>
    </>,
    document.body,
  )
}

// ── Detail Drawer (single SKU — Full matrix only) ─────────────────────────────

function DetailDrawer({ data, open, onClose, locationId }: { data: DrawerData | null; open: boolean; onClose: () => void; locationId: string | null }) {
  // Progressive reveal for the rival list — a calm default slice, not the full
  // wall. Reset whenever a different SKU is opened so it never opens expanded.
  const [showAllRivals, setShowAllRivals] = useState(false)
  useEffect(() => { setShowAllRivals(false) }, [data?.product, data?.competitor])

  if (!data) return null
  const verdict = youVerdict(data.yourPrice, data.theirPrice)
  const gap = data.yourPrice != null ? data.yourPrice - data.theirPrice : null
  const gapColor =
    gap == null || VERDICT_TONE[verdict] === 'neutral'
      ? 'var(--text-1)'
      : VERDICT_TONE[verdict] === 'danger'
        ? 'var(--danger)'
        : 'var(--accent)'

  // Rival list: compared rival always first, then a short default slice.
  const COLLAPSED_RIVALS = 5
  const orderedRivals = data.allPrices.slice().sort((a, b) => {
    if (a.name === data.competitor) return -1
    if (b.name === data.competitor) return 1
    return 0
  })
  const visibleRivals = showAllRivals ? orderedRivals : orderedRivals.slice(0, COLLAPSED_RIVALS)
  const hiddenRivals = orderedRivals.length - visibleRivals.length

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 340,
      background: 'var(--surface-solid)', borderLeft: '1px solid var(--border-2)',
      boxShadow: '-12px 0 40px rgba(0,0,0,0.18), -2px 0 8px rgba(0,0,0,0.06)',
      zIndex: 50, display: 'flex', flexDirection: 'column',
      transform: open ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s cubic-bezier(.2,.8,.2,1)',
    }}>
      <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <button
          onClick={onClose}
          style={{
            width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-3)',
            border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-2)', flexShrink: 0,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Pill variant="chip" tone={VERDICT_TONE[verdict]} style={{ marginBottom: 5 }}>{VERDICT_BADGE[verdict]}</Pill>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-2)', letterSpacing: '0.04em', marginBottom: 4 }}>
            vs {data.competitor}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em', lineHeight: 1.3 }}>
            {data.product}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', minHeight: 0 }}>
        {/* The gap is the one signal that drives a decision, so it leads. The two
            raw prices sit under it as a quiet supporting line rather than three
            equal boxes competing for attention. */}
        <div style={{ marginBottom: 14 }}>
          <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 32, fontWeight: 600, lineHeight: 1, color: gapColor }}>
            {gap == null ? '—' : `${gap > 0 ? '+' : gap < 0 ? '−' : ''}$${Math.abs(gap).toFixed(2)}`}
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-3)', marginTop: 5 }}>
            {gap == null ? 'No gap — you don’t carry this' : `Gap vs ${data.competitor}`}
          </div>
          {gap != null && (
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', marginTop: 9 }}>
              You <strong style={{ color: 'var(--text-1)', fontWeight: 700 }}>${data.yourPrice!.toFixed(2)}</strong>
              <span style={{ margin: '0 7px', opacity: 0.4 }}>·</span>
              Them <strong style={{ color: 'var(--text-1)', fontWeight: 700 }}>${data.theirPrice.toFixed(2)}</strong>
            </div>
          )}
        </div>
        {verdict === 'no_own_price' && (
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', marginBottom: 14, lineHeight: 1.5 }}>
            They’re at <strong style={{ color: 'var(--text-1)', fontWeight: 700 }}>${data.theirPrice.toFixed(2)}</strong>. You don’t carry this SKU (or it hasn’t been collected from your menu yet).
          </div>
        )}

        <PriceHistoryStrip locationId={locationId} productName={data.product} />

        <SectionLabel style={{ marginBottom: 8 }}>Rivals · vs your price</SectionLabel>
        <DataTable
          rows={visibleRivals}
          rowKey={(p) => p.name}
          columns={[
            {
              key: 'name',
              header: 'Rival',
              render: (p) => (p.name === data.competitor ? <strong>{p.name}</strong> : p.name),
            },
            {
              key: 'price',
              header: 'Price',
              render: (p) => <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>${p.price.toFixed(2)}</span>,
            },
            {
              key: 'vsyou',
              header: 'vs you',
              mono: false,
              render: (p) => {
                if (data.yourPrice == null) return <Pill variant="chip" tone="neutral">n/a</Pill>
                const v = youVerdict(data.yourPrice, p.price)
                const theirVsYou = p.price - data.yourPrice
                return (
                  <Pill variant="chip" tone={VERDICT_TONE[v]}>
                    {v === 'parity' ? 'even' : `${theirVsYou > 0 ? '+' : '−'}$${Math.abs(theirVsYou).toFixed(2)}`}
                  </Pill>
                )
              },
            },
          ] as Column<{ name: string; price: number }>[]}
        />
        {orderedRivals.length > COLLAPSED_RIVALS && (
          <button
            onClick={() => setShowAllRivals((v) => !v)}
            style={{
              marginTop: 8, width: '100%', padding: '7px 0', background: 'none',
              border: 'none', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 10,
              letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)',
            }}
          >
            {showAllRivals ? 'Show fewer' : `Show all ${orderedRivals.length} rivals · ${hiddenRivals} more`}
          </button>
        )}
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 7, flexShrink: 0 }}>
        <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>
          Dismiss
        </button>
        <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
          Log a decision
        </button>
      </div>
    </div>
  )
}

// ── Row action button ─────────────────────────────────────────────────────────

// Three identical white outlines read as one undifferentiated blob, so nothing
// invites a click. Each action gets its own tone from the CannaSpy palette:
// Assign is the primary move (teal), Snooze is a deferral (amber), Wrong match
// is a data correction (rose). Colour carries meaning here, not decoration.
type ActionTone = 'accent' | 'warm' | 'rose'

const ACTION_TONE: Record<ActionTone, { fg: string; border: string; tint: string }> = {
  accent: { fg: 'var(--accent)', border: 'var(--accent)', tint: 'var(--accent-soft)' },
  warm: { fg: 'var(--warm)', border: 'var(--warm)', tint: 'var(--warm-soft)' },
  rose: { fg: 'var(--rose)', border: 'var(--rose)', tint: 'var(--rose-soft)' },
}

function RowAction({
  children, onClick, disabled, active, testid, tone = 'accent', title,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
  active?: boolean
  testid: string
  tone?: ActionTone
  title?: string
}) {
  const t = ACTION_TONE[tone]
  return (
    <button
      type="button"
      data-testid={testid}
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: '3px 9px', borderRadius: 'var(--r-sm)', whiteSpace: 'nowrap',
        fontSize: 10.5, fontWeight: 700, fontFamily: 'var(--sans)',
        border: `1px solid ${active ? t.border : 'var(--border-2)'}`,
        background: active ? t.border : t.tint,
        color: active ? '#fff' : t.fg,
        cursor: disabled ? 'progress' : 'pointer',
        opacity: disabled ? 0.5 : 1, transition: 'var(--transition)',
      }}
    >
      {children}
    </button>
  )
}

// ── Assign-a-task dialog ──────────────────────────────────────────────────────
// Files a real annotation against the SKU's price_review_state row. The
// suggested wordings are computed from THIS row's actual numbers — no generic
// placeholder text — so one click produces a task a store manager can act on.

function suggestedTasks(r: QueueRow): string[] {
  const money = (n: number) => `$${n.toFixed(2)}`
  const out: string[] = []
  if (r.gapDollar != null && r.yourPrice != null) {
    const gap = Math.abs(r.gapDollar)
    if (r.gapDollar > 0) {
      out.push(`Lower our price ${money(gap)} on ${r.product} — we're at ${money(r.yourPrice)}, market is ${money(r.marketAvg)}`)
      if (r.firstRival) out.push(`Match ${r.firstRival.name}'s price of ${money(r.firstRival.price)} on ${r.product}`)
      out.push(`Hold our price on ${r.product} and record why`)
    } else {
      out.push(`Raise our price ${money(gap)} on ${r.product} — we're at ${money(r.yourPrice)}, market is ${money(r.marketAvg)}`)
      out.push(`Confirm we still want to undercut on ${r.product}`)
    }
  }
  if (r.rivals <= 1) out.push(`Check pricing on ${r.product} — only ${r.rivals} rival matched, thin evidence`)
  if (r.promoActive) out.push(`Review our promo position on ${r.product} — ${r.promoRivals} rival(s) currently running a deal`)
  return out
}

// Task wordings for a single product measured against ONE named rival — the
// rival drawer's context. suggestedTasks() talks about "the market", which is
// the wrong claim when the reader is looking at one competitor's price.
function rivalSkuTasks(product: string, rival: string, yourPrice: number, theirPrice: number, gap: number, onPromo: boolean): string[] {
  const money = (n: number) => `$${n.toFixed(2)}`
  const out: string[] = []
  if (gap > PARITY) {
    out.push(`Lower our price ${money(Math.abs(gap))} on ${product} — we're at ${money(yourPrice)}, ${rival} is at ${money(theirPrice)}`)
    out.push(`Match ${rival}'s price of ${money(theirPrice)} on ${product}`)
    out.push(`Hold our price on ${product} and record why we're above ${rival}`)
  } else if (gap < -PARITY) {
    out.push(`Confirm we still want to undercut ${rival} on ${product} — we're at ${money(yourPrice)}, they're at ${money(theirPrice)}`)
    out.push(`Raise our price ${money(Math.abs(gap))} on ${product} to match ${rival}`)
  } else {
    out.push(`Review ${product} — we're at parity with ${rival} at ${money(yourPrice)}`)
  }
  if (onPromo) out.push(`Check our promo position on ${product} — ${rival} is running a deal at ${money(theirPrice)}`)
  return out
}

// A matrix cell can be a SKU the operator does not carry — rivalSkuTasks assumes
// both prices exist. Fall back to catalog-gap wordings rather than inventing a
// difference that isn't there.
function cellTasks(product: string, rival: string, yourPrice: number | null, theirPrice: number, onPromo: boolean): string[] {
  const money = (n: number) => `$${n.toFixed(2)}`
  if (yourPrice == null) {
    const out = [
      `Decide whether to carry ${product} — ${rival} sells it at ${money(theirPrice)} and we don't stock it`,
      `Check supplier availability for ${product}`,
    ]
    if (onPromo) out.push(`${rival} is discounting ${product} to ${money(theirPrice)} — check whether it's worth carrying`)
    return out
  }
  return rivalSkuTasks(product, rival, yourPrice, theirPrice, yourPrice - theirPrice, onPromo)
}

function AssignTaskDialog({
  row, saving, error, onCancel, onSubmit, suggestions: override, locationId,
}: {
  row: QueueRow
  saving: boolean
  error: string | null
  onCancel: () => void
  onSubmit: (body: string, opts: { assigneeUserId: string | null; notify: NotifyOption }) => void
  /** Caller-supplied wordings. The rival drawer compares against ONE named
   *  rival, so "market is $x" would be a lie there — it passes its own. */
  suggestions?: string[]
  /** The SKU's location — scopes the eligible-assignee roster. */
  locationId: string | null
}) {
  const suggestions = override ?? suggestedTasks(row)
  const [body, setBody] = useState(suggestions[0] ?? `Review pricing on ${row.product}`)
  const { assignees, loading: assigneesLoading } = useEligibleAssignees(locationId)
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [notify, setNotify] = useState<NotifyOption>('none')
  const formError = validateAssignForm({ notify, assigneeUserId: assigneeId })

  return createPortal(
    <div
      data-testid="assign-scrim"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,0.28)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        data-testid="assign-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)', maxHeight: '86vh', overflow: 'auto',
          background: 'var(--surface-solid)', border: '1px solid var(--border-2)',
          borderRadius: 'var(--r-md)', boxShadow: '0 20px 60px rgba(0,0,0,0.28)', padding: 18,
        }}
      >
        <DetailPanel
          eyebrow="ASSIGN A TASK"
          title={row.product}
          style={{ marginBottom: 14 }}
        />

        <SectionLabel style={{ marginBottom: 7 }}>Task</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              data-testid="assign-suggestion"
              onClick={() => setBody(s)}
              style={{
                textAlign: 'left', padding: '7px 10px', borderRadius: 'var(--r-sm)',
                fontSize: 12, fontFamily: 'var(--sans)', cursor: 'pointer',
                border: `1.5px solid ${body === s ? 'var(--accent)' : 'var(--border)'}`,
                background: body === s ? 'var(--surface-3)' : 'transparent',
                color: 'var(--text-1)', transition: 'var(--transition)',
              }}
            >
              {s}
            </button>
          ))}
        </div>

        <SectionLabel style={{ marginBottom: 7 }}>Or write your own</SectionLabel>
        <textarea
          data-testid="assign-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box', marginBottom: 12, padding: '8px 10px',
            borderRadius: 'var(--r-sm)', border: '1px solid var(--border-2)',
            background: 'var(--surface)', color: 'var(--text-1)',
            fontSize: 12, fontFamily: 'var(--sans)', resize: 'vertical',
          }}
        />

        <div data-testid="assign-assignee" style={{ marginBottom: 12 }}>
          <MemberPicker assignees={assignees} value={assigneeId} onChange={setAssigneeId} loading={assigneesLoading} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <NotifySelect value={notify} onChange={setNotify} />
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginBottom: 16, fontFamily: 'var(--sans)' }}>
          Shows up on Team Annotations and Today. Notifications go only to the assigned teammate.
        </div>

        {error && (
          <div
            data-testid="assign-error"
            style={{
              marginBottom: 12, padding: '8px 10px', borderRadius: 'var(--r-sm)',
              border: '1px solid var(--danger)', color: 'var(--danger)',
              fontSize: 11.5, fontFamily: 'var(--sans)',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" data-testid="assign-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            data-testid="assign-save"
            disabled={saving || !body.trim() || !!formError}
            title={formError ?? undefined}
            onClick={() => onSubmit(body.trim(), { assigneeUserId: assigneeId, notify })}
            style={{ opacity: saving || !body.trim() || !!formError ? 0.5 : 1 }}
          >
            {saving ? 'Saving…' : 'Create task'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PriceIntelligence() {
  const authFetch = useAuthFetch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const requestedView = searchParams.get('view')
  const requestedProduct = searchParams.get('product')

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string | null>(() => searchParams.get('location_id'))
  const [view, setView] = useState<View>(() => requestedView === 'byrival' || requestedView === 'matrix' ? requestedView : 'priority')
  const [category, setCategory] = useState(() => searchParams.get('category') ?? '')
  // Alert deep-links seed the visible product search. This makes assortment
  // alerts land on their SKU instead of at the top of a multi-thousand-row
  // matrix while preserving the normal blank search for direct visits.
  const [search, setSearch] = useState(() => requestedProduct ?? '')
  const [pricePosition, setPricePosition] = useState<PricePosition>('all')
  // '' = all, UNBRANDED, or a brand name. Initialized from a `brand` deep-link
  // param (e.g. from Brand Carriers) and validated once the matrix loads so an
  // unmatched brand never leaves a dead filter (see the guard effect below).
  const [brandFilter, setBrandFilter] = useState(() => searchParams.get('brand') ?? '')
  const [scopeShared, setScopeShared] = useState(false)     // shared brands only (≥2 rivals)
  const [promoFilter, setPromoFilter] = useState<PromoFilter>('all')
  const [sortMode, setSortMode] = useState<SortMode>('recommended')
  // Metric-card class filter (volumeRisk / marginOpportunity / limitedEvidence)
  // — narrows the Needs-review table to one class. null = whole queue.
  const [classFilter, setClassFilter] = useState<ReviewClass | null>(null)
  // Permanent inspector selection (Needs review). Never opens the overlay drawer.
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  // Begin-review focus mode — client-side progress only, no persistence.
  const [reviewMode, setReviewMode] = useState(false)
  const [reviewIndex, setReviewIndex] = useState(0)
  // Full-matrix overlay drawer (Needs review uses the inspector instead).
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerData, setDrawerData] = useState<DrawerData | null>(null)
  const [rivalDrawerOpen, setRivalDrawerOpen] = useState(false)
  const [rivalDrawerData, setRivalDrawerData] = useState<RivalStat | null>(null)

  // Escape closes whichever overlay is on top (round 2 §16). The cell popover
  // owns its own capture-phase handler and stops propagation, so it always wins
  // when it is open; this covers the two drawers, which had no key handling at
  // all — the only way out was the Dismiss button or a click on the scrim.
  useEffect(() => {
    if (!drawerOpen && !rivalDrawerOpen && !selectedProduct) return
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Innermost first: the single-SKU inspector opens on top of By-rival, and
      // the Needs-review detail panel is the outermost of the three.
      if (drawerOpen) setDrawerOpen(false)
      else if (rivalDrawerOpen) setRivalDrawerOpen(false)
      else setSelectedProduct(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawerOpen, rivalDrawerOpen, selectedProduct])
  const [queueVisible, setQueueVisible] = useState(QUEUE_PAGE)
  const [rivalVisible, setRivalVisible] = useState(RIVAL_PAGE)
  const [operatorType, setOperatorType] = useState<OperatorType>('both')
  // By-rival view's own toolbar state (independent of the product filters).
  const [rivalSearch, setRivalSearch] = useState('')
  const [rivalPosition, setRivalPosition] = useState<RivalPosition | 'all'>('all')
  const [rivalMinShared, setRivalMinShared] = useState(0)
  const [rivalSort, setRivalSort] = useState<RivalSortMode>('pressure')
  // By-rival zero-comparable disclosure (round 2 §4).
  const [zeroCompOpen, setZeroCompOpen] = useState(false)
  // Matrix column-hover highlight (round 2 §12) — index of the hovered rival
  // column, or null. Set by pointer delegation on the table so a hover only
  // re-renders when it crosses a column boundary.
  // Matrix cell popover (round 2 §13) — the clicked competitor cell's context.
  const [cellPop, setCellPop] = useState<CellPopData | null>(null)
  // Matrix default scope: only SKUs the operator carries AND ≥1 rival prices.
  // A newly observed rival SKU is often not carried by the operator yet. An
  // alert deep-link must still reveal it, so do not apply the carried-SKU-only
  // default when a product was explicitly requested in the URL.
  const [matrixComparable, setMatrixComparable] = useState(() => !requestedProduct)
  // Seen live 2026-08-18: the matrix opened with 7 of 26 rivals and page after
  // page of rows that were nothing but em-dashes — 31 comparable products
  // inside 2,034 market SKUs. That is the "technical dump" reading. Four rivals
  // is a comparison you can hold in your head; the Rivals control adds more.
  // Matrix visible-rival selection. null = not yet initialized → default applied
  // once competitors load (all if ≤8, else Top 5 by pressure). Never hides silently.
  const [matrixRivalSel, setMatrixRivalSel] = useState<Set<string> | null>(null)
  // Below 900px the inspector stacks under the table.
  // Was a hand-rolled `window.innerWidth < 900`, so every width from 900 to
  // 1400 kept the full wide-desktop layout and simply squeezed. The stack
  // threshold moves to 1200: below that a 350px docked inspector leaves the
  // table too little room, and main ALREADY stacks the inspector below the
  // table, so reusing that branch needs no new visual treatment.
  //
  // Deliberately NOT an overlay drawer: an overlay needs an opaque fill and a
  // shadow, and --surface is translucent in dark mode. That would have meant
  // introducing colour, which is out of scope here.
  const bp = useBreakpoint()
  const isNarrow = bp.atMost('compact')   // <1200, matching the comment above
  // A 178px sticky actions column is 17% of a 1024px viewport, pinned open
  // while the rest of the table scrolls under it. Release it once the table is
  // the thing doing the scrolling. Boolean only — the sticky cell's own styles
  // are untouched.
  const stickyActions = bp.atLeast('desktop')
  // Matrix virtualization scroll tracking.
  const matrixScrollRef = useRef<HTMLDivElement>(null)
  const [matrixScrollTop, setMatrixScrollTop] = useState(0)
  // Horizontal scroll position + remaining travel. Drives the edge fades that
  // tell a first-time reader there ARE more rivals off-screen — without them a
  // 20-rival matrix looks like a 7-rival matrix.
  const [mxScrollX, setMxScrollX] = useState({ left: 0, max: 0 })
  // Seed the travel measurement without waiting for a first scroll — otherwise
  // the affordance the fade exists to provide is missing exactly when it is
  // needed, on the untouched screen. Re-measure when the column count or the
  // container width changes.
  const mxMeasureRef = useRef<() => void>(() => {})
  mxMeasureRef.current = () => {
    const el = matrixScrollRef.current
    if (!el) return
    const next = { left: el.scrollLeft, max: el.scrollWidth - el.clientWidth }
    // Bail when nothing moved: this runs from a ResizeObserver, and setting a
    // fresh object every pass would re-render forever.
    setMxScrollX((prev) => (prev.left === next.left && prev.max === next.max ? prev : next))
  }
  const [matrixViewportH, setMatrixViewportH] = useState(0)


  // ── Saved review decisions (migration 027) ─────────────────────────────────
  // Keyed by normalized product name — the SAME normalization the API applies
  // in productKey(). The client only ever READS by this key; every write sends
  // the display name and lets the server derive the key, so the two cannot
  // drift into disagreeing about which SKU a decision belongs to.
  const [reviewState, setReviewState] = useState<Map<string, ReviewStateRow>>(new Map())
  const [showHandled, setShowHandled] = useState(false)
  const [taskTarget, setTaskTarget] = useState<QueueRow | null>(null)
  // Set when the assign came from the rival drawer, where the comparison is
  // against one named rival rather than the market.
  const [taskSuggestions, setTaskSuggestions] = useState<string[] | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [taskSaving, setTaskSaving] = useState(false)
  const [taskError, setTaskError] = useState<string | null>(null)
  // SKUs the operator has acted on during THIS visit. They stay in the list
  // even once handled, so the tick, the "Snoozed" state and the status pill are
  // actually visible — and reversible — instead of the row vanishing the
  // instant it is clicked. Cleared on reload or when the location changes.
  const [justHandled, setJustHandled] = useState<Set<string>>(new Set())

  const { matrix, categories: categoryOptions, loading, error } = usePriceMatrix(selectedLocation, category || undefined, operatorType === 'both' ? undefined : operatorType)

  const loadReviewState = useCallback(async (locationId: string) => {
    try {
      const r = await authFetch(`${API}/api/v1/prices/review-state?location_id=${locationId}`)
      const d = await r.json()
      const rows: ReviewStateRow[] = d?.data?.states || []
      setReviewState(new Map(rows.map(s => [s.product_key, s])))
    } catch {
      // A failed read must not blank out decisions the operator already made —
      // leave the last known map in place rather than showing everything as new.
    }
  }, [authFetch])

  useEffect(() => {
    setJustHandled(new Set())
    if (!selectedLocation) { setReviewState(new Map()); return }
    loadReviewState(selectedLocation)
  }, [selectedLocation, loadReviewState])

  // Write one decision. Returns the saved row so the caller can attach a task
  // to it (annotations.entity_id) without a second lookup.
  const saveReviewState = useCallback(async (
    productName: string,
    state: ReviewState,
    snoozeDays?: number,
  ): Promise<ReviewStateRow | null> => {
    if (!selectedLocation) return null
    const key = reviewKey(productName)
    setSavingKey(key)
    try {
      const r = await authFetch(`${API}/api/v1/prices/review-state`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location_id: selectedLocation,
          product_name: productName,
          state,
          ...(snoozeDays ? { snooze_days: snoozeDays } : {}),
        }),
      })
      const d = await r.json()
      if (!r.ok || !d?.data) return null
      const row = d.data as ReviewStateRow
      setReviewState(prev => new Map(prev).set(row.product_key, row))
      setJustHandled(prev => new Set(prev).add(row.product_key))
      return row
    } catch {
      return null
    } finally {
      setSavingKey(null)
    }
  }, [authFetch, selectedLocation])

  useEffect(() => {
    if (category && categoryOptions.length > 0 && !categoryOptions.includes(category)) {
      setCategory('')
    }
  }, [category, categoryOptions])

  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then(r => r.json())
      .then(d => {
        const locs: Location[] = d.locations || []
        setLocations(locs)
        if (locs.length > 0) setSelectedLocation((current) => current && locs.some((location) => location.id === current) ? current : locs[0].id)
      })
      .catch(() => {})
  }, [authFetch])

  useLayoutEffect(() => {
    if (view !== 'matrix') return
    const el = matrixScrollRef.current
    if (!el) return
    const measure = () => setMatrixViewportH(el.clientHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [view])

  // ── Build data structures ──────────────────────────────────────────────────
  const productMap = new Map<string, Map<string, CellData>>()
  const competitorSet = new Set<string>()
  const productBrand = new Map<string, string | null>()
  const productCategory = new Map<string, string | null>()
  const productYourPrice = new Map<string, number | null>()
  const brandRivals = new Map<string, Set<string>>()
  // Rival operator type — present on every matrix row at runtime (business_type).
  const rivalBusinessType = new Map<string, 'storefront' | 'delivery' | null>()
  // Rival name → competitor_id, so the matrix cell popover can deep-link to the
  // rival's competitor profile (round 2 §13).
  const rivalCompetitorId = new Map<string, string>()

  for (const row of matrix) {
    competitorSet.add(row.competitor_name)
    if (!rivalBusinessType.has(row.competitor_name)) rivalBusinessType.set(row.competitor_name, row.business_type ?? null)
    if (!rivalCompetitorId.has(row.competitor_name)) rivalCompetitorId.set(row.competitor_name, row.competitor_id)
    if (!productMap.has(row.raw_name)) productMap.set(row.raw_name, new Map())
    productMap.get(row.raw_name)!.set(row.competitor_name, {
      price: row.price,
      inStock: true,
      onPromo: row.on_sale,
      lastUpdated: row.last_updated,
      gap: row.gap,
      verdict: row.verdict,
    })
    if (!productYourPrice.has(row.raw_name)) productYourPrice.set(row.raw_name, row.your_price)
    const b = (row.brand ?? '').trim()
    if (!productBrand.has(row.raw_name) || (productBrand.get(row.raw_name) == null && b)) {
      productBrand.set(row.raw_name, b || null)
    }
    if (!productCategory.has(row.raw_name) && row.category) productCategory.set(row.raw_name, row.category)
    if (b) {
      if (!brandRivals.has(b)) brandRivals.set(b, new Set())
      brandRivals.get(b)!.add(row.competitor_name)
    }
  }

  const competitors = [...competitorSet]

  // Per-product rival price stats (computed once over the full set, before filtering).
  const priceStats = new Map<string, { min: number; max: number; spread: number; avg: number }>()
  for (const [product, compMap] of productMap) {
    const prices = [...compMap.values()].map(v => v.price)
    const min = Math.min(...prices)
    const max = Math.max(...prices)
    const avg = prices.reduce((s, x) => s + x, 0) / prices.length
    priceStats.set(product, { min, max, spread: max - min, avg })
  }

  // Per-product you-vs-market verdict (your price vs the rival average, PARITY band).
  const productVerdict = (product: string): PriceVerdict =>
    youVerdict(productYourPrice.get(product) ?? null, priceStats.get(product)!.avg)

  // Per-product review classification — the single source of truth (lib/reviewQueue).
  const productClass = (product: string): ReviewClass =>
    classifySku({
      yourPrice: productYourPrice.get(product) ?? null,
      matchedRivalCount: productMap.get(product)!.size,
      marketAverage: priceStats.get(product)!.avg,
    })

  // Brands carried by ≥2 rivals = "shared".
  const sharedBrandSet = new Set([...brandRivals.entries()].filter(([, s]) => s.size >= 2).map(([b]) => b))
  const brandOptions = [...brandRivals.entries()]
    .sort((a, b) => b[1].size - a[1].size || a[0].localeCompare(b[0]))
    .map(([b, s]) => ({ value: b, label: b, rivals: s.size }))
  const hasUnbranded = [...productBrand.values()].some(v => v == null)

  // Drop a deep-linked brand that has no matched SKUs at this location once the
  // matrix has loaded, so a `?brand=` param never renders as a filter that
  // silently hides every row. UNBRANDED is always valid when unbranded SKUs exist.
  useEffect(() => {
    if (!brandFilter || matrix.length === 0) return
    if (brandFilter === UNBRANDED) { if (!hasUnbranded) setBrandFilter(''); return }
    if (!brandOptions.some(b => b.value === brandFilter)) setBrandFilter('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandFilter, matrix])

  let products = [...productMap.keys()]

  // Search — product name OR brand contains the query (client-side).
  const q = search.trim().toLowerCase()
  if (q) {
    products = products.filter(p =>
      p.toLowerCase().includes(q) || (productBrand.get(p) || '').toLowerCase().includes(q))
  }

  // Brand scope (shared-only) + brand selection
  if (scopeShared) {
    products = products.filter(p => {
      const b = productBrand.get(p)
      return b != null && sharedBrandSet.has(b)
    })
  }
  if (brandFilter === UNBRANDED) {
    products = products.filter(p => productBrand.get(p) == null)
  } else if (brandFilter) {
    products = products.filter(p => productBrand.get(p) === brandFilter)
  }

  // Price position — your price vs the market average (epsilon-safe).
  if (pricePosition !== 'all') {
    products = products.filter(p => {
      const yp = productYourPrice.get(p) ?? null
      if (pricePosition === 'notcarried') return yp == null
      if (yp == null) return false
      const gap = yp - priceStats.get(p)!.avg
      if (pricePosition === 'above') return gap > MARKET_EPSILON
      if (pricePosition === 'below') return gap < -MARKET_EPSILON
      return Math.abs(gap) <= MARKET_EPSILON   // 'at'
    })
  }

  // Promo filter (any rival on promo for this product)
  if (promoFilter !== 'all') {
    products = products.filter(p => {
      const anyPromo = [...productMap.get(p)!.values()].some(d => d.onPromo)
      return promoFilter === 'promo' ? anyPromo : !anyPromo
    })
  }

  // Matrix scope (Matrix view only — never narrows the queue).
  //
  // "Comparable SKUs only" used to mean "products YOU carry", which is why the
  // live matrix on 2026-08-18 was a field of em-dashes: a product you stock that
  // none of the SHOWN rivals carry still earned a full row, every cell empty.
  // With 7 of 26 rivals visible that is most of the catalogue. A row in a
  // comparison grid should be a comparison, so a product now has to be carried
  // by at least one rival that is actually on screen.
  if (view === 'matrix' && matrixComparable) {
    const visible = matrixRivalSel
    products = products.filter(p => {
      if (productYourPrice.get(p) == null) return false
      const m = productMap.get(p)
      if (!m || m.size === 0) return false
      if (!visible) return true
      for (const rival of m.keys()) if (visible.has(rival)) return true
      return false
    })
  }

  // Saved decisions drop out of the review scope entirely — not just the table,
  // but the hero count, the three metrics and the category bars too, so they all
  // still reconcile (the invariant lib/reviewQueue's unit test guards). Only the
  // Needs-review view narrows; Matrix and By-rival keep the full catalogue.
  const stateFor = (p: string): ReviewStateRow | undefined => reviewState.get(reviewKey(p))
  const isHandled = (p: string): boolean => {
    const s = stateFor(p)
    return !!s && HANDLED_STATES.has(s.state)
  }
  const handledInScope = products.filter(isHandled).length
  if (view === 'priority' && !showHandled) {
    products = products.filter(p => !isHandled(p) || justHandled.has(reviewKey(p)))
  }

  const totalSkus = productMap.size

  // ── Review-queue classification over the CURRENT FILTER SCOPE ────────────────
  // `products` at this point is the toolbar-filtered scope (Needs-review view never
  // applies the matrix-comparable filter). summarize() guarantees the hero count
  // equals volumeRisk + marginOpportunity + limitedEvidence exactly.
  const counts = summarize(products.map(p => ({
    yourPrice: productYourPrice.get(p) ?? null,
    matchedRivalCount: productMap.get(p)!.size,
    marketAverage: priceStats.get(p)!.avg,
  })))

  // The queue = the three actionable classes. classFilter narrows the visible rows.
  const queueProducts = products.filter(p => inQueue(productClass(p)))
  const displayQueueProducts = classFilter
    ? queueProducts.filter(p => productClass(p) === classFilter)
    : queueProducts

  // Volume-risk products by category (inspector count bars). ONE definition,
  // (The per-category volume-risk breakdown that used to be computed here was
  // removed with the inspector block it fed — see the note at that JSX site. It
  // was a queue-wide aggregate duplicating the ribbon's volumeRisk count.)

  const locationName = locations.find(l => l.id === selectedLocation)?.name || 'Location'
  const lastUpdated = matrix.length > 0 ? timeAgo(matrix[0].last_updated) : '—'
  const money = (n: number) => `$${n.toFixed(2)}`

  const openDrawer = useCallback((product: string, competitor: string, theirPrice: number) => {
    const compMap = productMap.get(product)
    const allPrices = compMap
      ? [...compMap.entries()].map(([name, d]) => ({ name, price: d.price }))
      : []
    setDrawerData({
      product, competitor, theirPrice,
      yourPrice: productYourPrice.get(product) ?? null,
      allPrices, onPromo: compMap?.get(competitor)?.onPromo ?? false,
    })
    setDrawerOpen(true)
  }, [productMap, productYourPrice])

  // Open the matrix cell popover anchored at the clicked cell (round 2 §13).
  const openCellPopover = useCallback((e: React.MouseEvent, product: string, competitor: string, theirPrice: number, onPromo: boolean) => {
    e.stopPropagation()
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setCellPop({
      product, competitor,
      competitorId: rivalCompetitorId.get(competitor) ?? null,
      theirPrice, yourPrice: productYourPrice.get(product) ?? null,
      onPromo, x: r.left, y: r.bottom, yTop: r.top,
    })
  }, [rivalCompetitorId, productYourPrice])

  const sep = <div style={{ width: 1, height: 20, background: 'var(--border-2)', flexShrink: 0, margin: '0 4px' }} />

  // ── Needs-review rows (built from the displayed queue) ───────────────────────
  const queueRowFor = (product: string): QueueRow => {
    const compMap = productMap.get(product)!
    const stats = priceStats.get(product)!
    const yp = productYourPrice.get(product) ?? null
    const gapDollar = yp == null ? null : yp - stats.avg
    const gapPct = yp == null || stats.avg === 0 ? null : ((yp - stats.avg) / stats.avg) * 100
    const cellVals = [...compMap.values()]
    const promoRivals = cellVals.filter(d => d.onPromo).length
    const entries = [...compMap.entries()]
    return {
      product,
      brand: productBrand.get(product) ?? null,
      category: productCategory.get(product) ?? null,
      rivals: compMap.size,
      yourPrice: yp,
      marketAvg: stats.avg,
      rivalLow: stats.min,
      gapDollar,
      gapPct,
      reviewClass: productClass(product),
      promoActive: promoRivals > 0,
      promoRivals,
      firstRival: entries.length ? { name: entries[0][0], price: entries[0][1].price } : null,
    }
  }

  const unsortedQueueRows = displayQueueProducts.map(queueRowFor)

  // ── Recommended order — deterministic, no AI ────────────────────────────────
  // Sort key, in order:
  //   1. class rank: volumeRisk < marginOpportunity < limitedEvidence
  //   2. absolute dollar gap, descending
  //   3. matchedRivalCount, descending
  //   4. product name ascending (stable tiebreak)
  const absGap = (r: QueueRow) => Math.abs(r.gapDollar ?? 0)
  const absPct = (r: QueueRow) => Math.abs(r.gapPct ?? 0)
  const queueComparators: Record<SortMode, (a: QueueRow, b: QueueRow) => number> = {
    recommended: (a, b) =>
      (CLASS_RANK[a.reviewClass] - CLASS_RANK[b.reviewClass]) ||
      (absGap(b) - absGap(a)) ||
      (b.rivals - a.rivals) ||
      a.product.localeCompare(b.product),
    gap: (a, b) => (absGap(b) - absGap(a)) || a.product.localeCompare(b.product),
    pct: (a, b) => (absPct(b) - absPct(a)) || a.product.localeCompare(b.product),
    rivals: (a, b) => (b.rivals - a.rivals) || a.product.localeCompare(b.product),
    promo: (a, b) => (Number(b.promoActive) - Number(a.promoActive)) || (absGap(b) - absGap(a)) || a.product.localeCompare(b.product),
    brand: (a, b) => (a.brand || UNBRANDED_LABEL).localeCompare(b.brand || UNBRANDED_LABEL) || a.product.localeCompare(b.product),
  }
  const queueRows = [...unsortedQueueRows].sort(queueComparators[sortMode])
  const topQueueRow = queueRows[0] ?? null
  // `queueRows` stays the source of truth — review mode steps through it, the
  // ribbon counts it, "Begin review" starts at its head. Only the RENDERED set
  // is capped, so batching the view never narrows an action.
  const queueShown = queueRows.slice(0, queueVisible)

  // Any change to the filter set restarts both batches; otherwise a narrowed
  // result inherits an expanded count and the control silently disappears.
  useEffect(() => { setQueueVisible(QUEUE_PAGE) }, [search, pricePosition, category, brandFilter, promoFilter, scopeShared, sortMode, classFilter, operatorType, showHandled])
  useEffect(() => { setRivalVisible(RIVAL_PAGE) }, [rivalSearch, rivalPosition, rivalMinShared, rivalSort, operatorType])

  // Selection no longer auto-starts. It used to seed the first queued row so the
  // permanently docked inspector had something to show; now that the panel is an
  // overlay, doing that would cover a quarter of the table before the operator
  // has clicked anything. The queue opens clear, and a stale selection (one
  // filtered out from under you) is dropped rather than replaced.
  const selectionValid = selectedProduct != null && displayQueueProducts.includes(selectedProduct)
  useEffect(() => {
    if (view !== 'priority') return
    if (selectionValid) return
    setSelectedProduct(null)
  }, [view, selectionValid])

  // Focus-mode index → selection sync. reviewIndex indexes the sorted queueRows.
  useEffect(() => {
    if (!reviewMode) return
    const clamped = Math.max(0, Math.min(reviewIndex, queueRows.length - 1))
    if (clamped !== reviewIndex) { setReviewIndex(clamped); return }
    const p = queueRows[clamped]?.product ?? null
    if (p && p !== selectedProduct) setSelectedProduct(p)
  }, [reviewMode, reviewIndex, queueRows, selectedProduct])

  // Status cell. Was a tinted Pill: 9px mono on a 10–18%-alpha wash, which is
  // precisely the "small text on pale shaded backgrounds" the founder kept
  // reading as weak (2026-08-18). A dot carries the colour at full strength and
  // the label sits on the row's own background at normal text weight, so the
  // meaning survives and the wash goes away.
  const statusPill = (cls: ReviewClass) => (
    <span
      title={CLASS_STATUS_HELP[cls]}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: CLASS_COLOR[cls], flexShrink: 0 }} />
      <span style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap' }}>
        {CLASS_STATUS_LABEL[cls]}
      </span>
    </span>
  )

  // Needs-review columns. Gap is deliberately third — the number that makes the
  // row worth reading comes early. Numbers right-aligned mono .tnum; text left.
  // ── Row actions ────────────────────────────────────────────────────────────
  // Every one of these is a write, so each is a single call and the row shows a
  // saving state — nothing is applied optimistically and then silently lost.
  const toggleReviewed = (product: string) => {
    const current = stateFor(product)?.state
    void saveReviewState(product, current === 'reviewed' ? 'open' : 'reviewed')
  }
  const toggleSnooze = (product: string) => {
    const current = stateFor(product)?.state
    if (current === 'snoozed') void saveReviewState(product, 'open')
    else void saveReviewState(product, 'snoozed', SNOOZE_DAYS)
  }
  const toggleNotComparable = (product: string) => {
    const current = stateFor(product)?.state
    void saveReviewState(product, current === 'not_comparable' ? 'open' : 'not_comparable')
  }

  // Filing a task does NOT change the review decision — it only needs the
  // review row to exist so the annotation has something to hang off. So the
  // upsert re-sends whatever state the SKU is already in.
  const submitTask = async (bodyText: string, opts: { assigneeUserId: string | null; notify: NotifyOption }) => {
    if (!taskTarget || !selectedLocation) return
    setTaskSaving(true)
    setTaskError(null)
    try {
      const row = await saveReviewState(taskTarget.product, stateFor(taskTarget.product)?.state ?? 'open')
      if (!row) { setTaskError("Couldn't save against this product. Nothing was created — try again."); return }

      const res = await authFetch(`${API}/api/v1/annotations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: bodyText,
          entity_type: 'price_review',
          entity_id: row.id,
          assignee_id: opts.assigneeUserId,
          notify: opts.notify,
        }),
      })
      const d = await res.json().catch(() => null)
      if (!res.ok) {
        setTaskError(d?.error || "Couldn't create the task. Nothing was saved — try again.")
        return
      }
      // Delivery outcome (d.data.deliveries) is surfaced on the Action Queue via
      // the durable task_notifications record; nothing more to show here.
      await loadReviewState(selectedLocation)
      setTaskTarget(null)
      setTaskSuggestions(null)
    } catch {
      setTaskError("Couldn't reach the server. Nothing was saved — try again.")
    } finally {
      setTaskSaving(false)
    }
  }

  const queueColumns: Column<QueueRow>[] = [
    {
      // Tick box. Deliberately a real button with an accessible name rather than
      // a bare glyph — the row itself is clickable (it drives the inspector), so
      // this must stop propagation or ticking would also move the selection.
      key: 'done', header: '', align: 'center', mono: false, width: 36, minWidth: 36,
      render: (r) => {
        const st = stateFor(r.product)?.state
        const done = st === 'reviewed'
        const busy = savingKey === reviewKey(r.product)
        return (
          <button
            type="button"
            data-testid="review-tick"
            aria-label={done ? `Mark ${r.product} as not reviewed` : `Mark ${r.product} reviewed`}
            aria-pressed={done}
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); toggleReviewed(r.product) }}
            style={{
              width: 16, height: 16, borderRadius: 4, padding: 0, cursor: busy ? 'progress' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1.5px solid ${done ? 'var(--accent)' : 'var(--border-2)'}`,
              background: done ? 'var(--accent)' : 'transparent',
              opacity: busy ? 0.5 : 1, transition: 'var(--transition)',
            }}
          >
            {done && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
        )
      },
    },
    {
      key: 'product', header: 'Product', align: 'left', mono: false, width: '40%', minWidth: 200, sortable: true,
      sortValue: (r) => r.product.toLowerCase(),
      render: (r) => (
        <div style={{ minWidth: 0 }}>
          <span title={r.product} style={{ display: 'block', maxWidth: 320, fontWeight: 600, fontSize: 12.5, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.product}
          </span>
          <span style={{ display: 'block', maxWidth: 320, color: 'var(--text-3)', fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.brand || UNBRANDED_LABEL}
          </span>
        </div>
      ),
    },
    {
      key: 'status', header: 'Status', align: 'left', mono: false, width: '18%', minWidth: 150, sortable: true,
      sortValue: (r) => CLASS_RANK[r.reviewClass],
      render: (r) => {
        const st = stateFor(r.product)?.state
        // When handled rows are shown, the decision is what matters — say which
        // one it was, and when a snooze runs out, rather than repeating the
        // price class the operator has already dealt with.
        if (st && st !== 'open' && HANDLED_STATES.has(st)) {
          const until = stateFor(r.product)?.snoozed_until
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--text-3)', flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                {STATE_LABEL[st as Exclude<ReviewState, 'open'>]}
              </span>
              {st === 'snoozed' && until && (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
                  back {new Date(until).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              )}
            </span>
          )
        }
        return statusPill(r.reviewClass)
      },
    },
    {
      key: 'gap', header: 'Gap', align: 'right', width: '12%', minWidth: 96, sortable: true, sortValue: (r) => r.gapDollar ?? -Infinity,
      render: (r) => {
        if (r.gapDollar == null) return <span style={{ color: 'var(--text-3)' }}>—</span>
        return <span style={{ color: CLASS_COLOR[r.reviewClass], fontWeight: 700 }}>{signedMoney(r.gapDollar)}</span>
      },
    },
    {
      key: 'yourPrice', header: 'Your price', align: 'right', width: '14%', minWidth: 110, sortable: true, sortValue: (r) => r.yourPrice ?? -Infinity,
      render: (r) => (r.yourPrice == null ? <span style={{ color: 'var(--text-3)' }}>—</span> : money(r.yourPrice)),
    },
    {
      key: 'marketAvg', header: 'Market average', align: 'right', width: '16%', minWidth: 130, sortable: true, sortValue: (r) => r.marketAvg,
      render: (r) => <span style={{ color: 'var(--text-2)' }}>{money(r.marketAvg)}</span>,
    },
      // Lowest rival / Rivals / Category / Promotion were cut from the queue
      // table (founder review of the live page, 2026-08-18). Nine
      // always-visible columns for a decision that turns on four is a
      // spreadsheet, not a work queue. What survives is the question the row
      // asks — WHAT is it, HOW bad, BY HOW MUCH, and AGAINST WHAT. The other
      // four are all still one click away in the detail panel, which is the
      // whole reason that panel exists.
      // The actions column is gone. It carried three buttons on EVERY row —
      // 233px of permanent width, roughly a quarter of the table at 1440,
      // spent repeating the same three controls a few hundred times, and
      // pinned sticky-right so it sat on top of the data. Founder review
      // 2026-08-18: "action items on the right are interfering with the
      // table". The actions now live in the detail panel, with the one
      // product they act on. The inline checkbox stays: ticking a row done
      // is a bulk gesture, not a decision about one SKU.
  ]

  // ── By-rival stats ──────────────────────────────────────────────────────────
  // Computed once per rival over its SHARED SKUs (SKUs you both carry), from the
  // full catalogue — the By-rival view has its own toolbar and does NOT inherit
  // the product-level filters. All numbers follow the s07 deterministic formulas.
  const allProducts = [...productMap.keys()]
  const rivalStats: RivalStat[] = competitors.map((competitor) => {
    const skus: RivalSku[] = allProducts
      .filter((p) => productMap.get(p)?.has(competitor) && productYourPrice.get(p) != null)
      .map((p) => {
        const d = productMap.get(p)!.get(competitor)!
        const yourPrice = productYourPrice.get(p) as number
        return { product: p, category: productCategory.get(p) ?? null, brand: productBrand.get(p) ?? null, yourPrice, theirPrice: d.price, gap: yourPrice - d.price, onPromo: d.onPromo }
      })
      .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap))
    const shared = skus.length
    const higher = skus.filter((s) => s.gap > PARITY).length
    const lower = skus.filter((s) => s.gap < -PARITY).length
    const parity = skus.filter((s) => Math.abs(s.gap) <= PARITY).length
    const avgDiff = shared ? skus.reduce((sum, s) => sum + s.gap, 0) / shared : null
    const maxDiff = shared ? skus.reduce((m, s) => (Math.abs(s.gap) > Math.abs(m) ? s.gap : m), skus[0].gap) : null
    const position = rivalPositionOf(shared, higher, lower, parity)

    // Per-category pressure — over this rival's shared set, sorted by higherInCat.
    const catMap = new Map<string, { inCat: number; higherInCat: number }>()
    for (const s of skus) {
      const c = s.category || 'Other'
      const e = catMap.get(c) || { inCat: 0, higherInCat: 0 }
      e.inCat += 1
      if (s.gap > PARITY) e.higherInCat += 1
      catMap.set(c, e)
    }
    const categoryPressure = [...catMap.entries()]
      .map(([category, e]) => ({ category, ...e }))
      .sort((a, b) => b.higherInCat - a.higherInCat || b.inCat - a.inCat || a.category.localeCompare(b.category))

    return {
      competitor,
      businessType: rivalBusinessType.get(competitor) ?? null,
      shared, higher, lower, parity, avgDiff, maxDiff, position,
      sentence: rivalSentence(position, competitor, shared, higher, lower, parity),
      pressure: rivalPressure(shared, higher, avgDiff),
      skus,
      categoryPressure,
    }
  })

  const operatorTypeLabel = (t: 'storefront' | 'delivery' | null): string =>
    t === 'storefront' ? 'Storefront' : t === 'delivery' ? 'Delivery' : '—'

  const byRivalColumns: Column<RivalStat>[] = [
    {
      // Width-capped so it stops absorbing every spare pixel and stranding
      // Shared SKUs at the far edge (founder review 2026-08-18). A percentage
      // rather than a fixed px so it still breathes at 1700.
      key: 'competitor', header: 'Rival', align: 'left', mono: false, width: '30%', minWidth: 190, sortable: true,
      sortValue: (r) => r.competitor.toLowerCase(),
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
          <span style={{ minWidth: 0 }}>
            {/* Fills the column instead of truncating at a fixed 240px. The
                hard cap clipped "HAVEN Cannabis Marijuana and Weed Dispensary
                – San Bernardino" 140px short of the column edge, so every row
                left a matching hole between the name and Shared SKUs. */}
            <span title={r.competitor} style={{ display: 'block', maxWidth: '100%', fontWeight: 600, fontSize: 12.5, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.competitor}
            </span>
            <span style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.04em', color: 'var(--text-3)' }}>
              {operatorTypeLabel(r.businessType)}
            </span>
          </span>
        </span>
      ),
    },
    {
      // Founder review 2026-08-18: the gap between Rival and this column read as
      // a hole, and the count sat hard against its right edge. Centred, and
      // width-capped so it sits next to the rival it describes — the Rival
      // column is capped in turn (below) so it stops absorbing all the slack.
      key: 'shared', header: 'Shared SKUs', align: 'center', sortable: true, sortValue: (r) => r.shared,
      width: '9%', minWidth: 88,
      render: (r) => <span className="tnum" style={{ color: 'var(--text-1)', fontWeight: 600 }}>{r.shared}</span>,
    },
    {
      // Relationship — round 2 §3: one compact label, the detailed sentence
      // demoted to a tooltip + accessible title (also in the drawer). No
      // per-row prose to scan past. Sorts by the deterministic pressure score.
      // 11%, not 18%: at 18% the pill (~110px) left ~90px of dead space before
      // the split bars — the "awkward empty gap between columns" from founder
      // review 2026-08-18. Sized to the pill so the bars start right after it.
      key: 'position', header: 'Price position', align: 'left', mono: false, width: '11%', minWidth: 128, sortable: true,
      sortValue: (r) => r.pressure,
      render: (r) => (
        <span
          data-testid="rival-position"
          title={r.sentence}
          aria-label={`${RIVAL_POSITION_LABEL[r.position]}. ${r.sentence}`}
          style={{ display: 'block', minWidth: 0 }}
        >
          <Pill variant="chip" tone={RIVAL_POSITION_TONE[r.position]}>{RIVAL_POSITION_LABEL[r.position]}</Pill>
        </span>
      ),
    },
    {
      // Distribution — round 2 §2: the bar carries higher/parity/lower, and the
      // three counts live directly beneath it (the separate numeric columns are
      // gone). Bar keeps its parseable aria-label for the geometry spec.
      key: 'dist', header: 'How the prices split', align: 'left', mono: false, width: '33%', minWidth: 190,
      render: (r) => (
        <span style={{ display: 'block', minWidth: 0 }}>
          <DistributionBar higher={r.higher} parity={r.parity} lower={r.lower} shared={r.shared} />
          <span className="tnum" style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)', marginTop: 3, whiteSpace: 'nowrap' }}>
            <span style={{ color: r.higher > 0 ? 'var(--danger)' : 'var(--text-3)' }}>{r.higher} higher</span>
            {' · '}{r.parity} parity{' · '}
            <span style={{ color: r.lower > 0 ? 'var(--accent)' : 'var(--text-3)' }}>{r.lower} lower</span>
          </span>
        </span>
      ),
    },
      // "Net avg. difference" is gone. Two money columns saying nearly the
      // same thing is what left the 300px void between Distribution and the
      // right edge (seen live 2026-08-18), and the label was not plain
      // English. Largest difference is the one you can act on.
    {
      // Largest difference — round 2 §5: directional words, not a bare sign.
      key: 'maxDiff', header: 'Largest difference', align: 'right', mono: false, width: '17%', minWidth: 132, sortable: true,
      sortValue: (r) => (r.maxDiff == null ? -Infinity : Math.abs(r.maxDiff)),
      render: (r) => (r.maxDiff == null
        ? <span style={{ color: 'var(--text-3)' }}>—</span>
        : <span style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, color: diffColor(r.maxDiff), whiteSpace: 'nowrap' }}>{diffShort(r.maxDiff)}</span>),
    },
  ]

  // ── By-rival filters + sort (this view's own toolbar) ───────────────────────
  const rivalQuery = rivalSearch.trim().toLowerCase()
  const byRivalFiltered = rivalStats.filter((r) => {
    if (rivalQuery && !r.competitor.toLowerCase().includes(rivalQuery)) return false
    if (rivalPosition !== 'all' && r.position !== rivalPosition) return false
    if (r.shared < rivalMinShared) return false
    return true
  })
  const rivalComparators: Record<RivalSortMode, (a: RivalStat, b: RivalStat) => number> = {
    pressure: (a, b) => b.pressure - a.pressure || a.competitor.localeCompare(b.competitor),
    shared: (a, b) => b.shared - a.shared || a.competitor.localeCompare(b.competitor),
    avgDiff: (a, b) => Math.abs(b.avgDiff ?? 0) - Math.abs(a.avgDiff ?? 0) || a.competitor.localeCompare(b.competitor),
    maxDiff: (a, b) => Math.abs(b.maxDiff ?? 0) - Math.abs(a.maxDiff ?? 0) || a.competitor.localeCompare(b.competitor),
    higher: (a, b) => b.higher - a.higher || a.competitor.localeCompare(b.competitor),
    name: (a, b) => a.competitor.localeCompare(b.competitor),
  }
  const byRivalRows = [...byRivalFiltered].sort(rivalComparators[rivalSort])
  // Round 2 §4: rivals with zero comparable SKUs must not carry the same visual
  // weight as ones you can actually read. Keep them searchable and accessible,
  // but demote them to a collapsed, keyboard-expandable group beneath the table.
  const byRivalMain = byRivalRows.filter((r) => r.shared > 0)
  const byRivalShown = byRivalMain.slice(0, rivalVisible)
  const byRivalZero = byRivalRows.filter((r) => r.shared === 0)

  const openRivalDrawer = useCallback((r: RivalStat) => {
    setRivalDrawerData(r)
    setRivalDrawerOpen(true)
  }, [])

  // The page used to build its own active-filter chip row here. Live review at
  // 1440 (2026-08-17) showed the result: "Shared brands only" rendered TWICE —
  // once as a FilterBar chip in the control band, once as an accent pill in a
  // separate ~35px band above the table. Everything that row covered is now
  // visible where it is set: demoted filters as FilterBar chips, search in the
  // search box, price position in its own dropdown (which goes accent when
  // set), and the review class in the ribbon, which already offers "Clear".
  // The extra band is gone with it.

  // Matrix row order. Alphabetical order on a sparse grid stacks a run of
  // "not carried" rows at the top and the board reads as broken rather than as
  // scoped — the live screen on 2026-08-18 opened on four rows that were almost
  // entirely em-dashes. Ordering by how many of the SHOWN rivals actually carry
  // the SKU puts the real comparison on the first screen. Matrix only; the
  // review queue and By-rival keep their own sorts.
  const matrixOrdered = view !== 'matrix' ? products : [...products].sort((a, b) => {
    const cover = (prod: string): number => {
      const m = productMap.get(prod)
      if (!m) return 0
      if (!matrixRivalSel) return m.size
      let n = 0
      for (const c of m.keys()) if (matrixRivalSel.has(c)) n++
      return n
    }
    return cover(b) - cover(a) || a.localeCompare(b)
  })

  // ── Matrix virtual window ───────────────────────────────────────────────────
  // 34, not 30: at 30 the 13px price and its 11px delta sat on the row's
  // baseline with ~8px of air, and a 5-wide grid of them read as a wall of
  // digits. The extra 4px is the cheapest legibility the view can buy.
  const MATRIX_ROW = 34
  const MATRIX_OVERSCAN = 12
  const mTotal = matrixOrdered.length
  const mVisible = Math.ceil((matrixViewportH || 800) / MATRIX_ROW) + MATRIX_OVERSCAN * 2
  const mStart = Math.max(0, Math.min(
    Math.floor(matrixScrollTop / MATRIX_ROW) - MATRIX_OVERSCAN,
    Math.max(0, mTotal - mVisible),
  ))
  const mEnd = Math.min(mTotal, mStart + mVisible)
  const mTopPad = mStart * MATRIX_ROW
  const mBotPad = Math.max(0, (mTotal - mEnd) * MATRIX_ROW)
  const matrixWindow = matrixOrdered.slice(mStart, mEnd)

  function matrixKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const el = matrixScrollRef.current
    if (!el) return
    const page = Math.max(MATRIX_ROW, el.clientHeight - MATRIX_ROW)
    switch (e.key) {
      case 'ArrowDown': el.scrollTop += MATRIX_ROW; break
      case 'ArrowUp': el.scrollTop -= MATRIX_ROW; break
      case 'PageDown': el.scrollTop += page; break
      case 'PageUp': el.scrollTop -= page; break
      case 'Home': el.scrollTop = 0; break
      case 'End': el.scrollTop = el.scrollHeight; break
      default: return
    }
    e.preventDefault()
  }

  // ── Matrix rival selection ──────────────────────────────────────────────────
  const rivalStatByName = new Map(rivalStats.map((r) => [r.competitor, r]))
  // Top 5 rivals by pressure — used by the selector's "Top 5" action and the
  // default when a store tracks more than 8 rivals.
  const MX_PRODUCT_W = 240
  const MX_PRICE_W = 84
  // Position labels are now plain two-word verdicts ("Above rivals" / "Below
  // rivals" / "Mixed" / "At parity"), not "Higher than 2 of 2" + a weak-evidence
  // icon. The widest chip ("Below rivals") needs ~115px with cell padding, so
  // the column drops 156 → 130 and the frozen block lightens 480 → 454.
  const MX_POS_W = 130
  const MX_RIVAL_W = 150
  // Three, not four or five. The board's job on open is "who am I priced
  // against", not "every rival at once" — three wide columns scan in a glance
  // and each carries more shared SKUs, so the opening grid has fewer em-dashes.
  // Rivals ▾ opens the other 23. (Founder ranked Full matrix as still too dense
  // — the fix is fewer columns by default, not more signal per cell.)
  const MX_DEFAULT_RIVALS = 3

  // How many rival columns fit beside the frozen block at the current width.
  // Floor of 5 so a narrow window still shows a usable comparison.
  const rivalFitCount = (): number => {
    const el = matrixScrollRef.current
    const avail = (el?.clientWidth ?? (typeof window !== 'undefined' ? window.innerWidth - 64 : 1440)) - (MX_PRODUCT_W + MX_PRICE_W + MX_POS_W)
    return Math.max(5, Math.floor(avail / MX_RIVAL_W))
  }

  const rivalsByPressure = [...rivalStats].sort((a, b) => b.pressure - a.pressure).map((r) => r.competitor)
  const topRivalNames = rivalsByPressure.slice(0, 5)
  // The DEFAULT columns are picked by shared-SKU coverage, not by pressure.
  // Pressure ranked the 26-rival account onto five rivals sharing 6/4/3/4/3
  // SKUs while two rivals sharing 8 sat hidden, so the opening grid was ~70%
  // em-dashes. Coverage first, pressure as the tie-break: the four columns you
  // land on are the four you can actually compare against.
  const rivalsByCoverage = [...rivalStats]
    .sort((a, b) => b.shared - a.shared || b.pressure - a.pressure || a.competitor.localeCompare(b.competitor))
    .map((r) => r.competitor)
  const competitorKey = competitors.join('|')
  useEffect(() => {
    setMatrixRivalSel((prev) => {
      // Default to as many rivals as actually FIT, ranked by pressure — not a
      // fixed 5. On a 26-rival account a hard Top-5 left 516px of empty grey
      // beside the table while 21 rivals sat hidden, which reads as a broken
      // screen rather than as a deliberate shortlist.
      // Capped at 5 regardless of what fits. "As many as fit" is how a
      // comparison board becomes a dump — the width is not the constraint,
      // the reader is. Ranked by pressure, so the five shown are the five
      // that matter most; Rivals ▾ reveals the rest.
      const def = () => new Set((rivalsByCoverage.length ? rivalsByCoverage : competitors).slice(0, Math.min(MX_DEFAULT_RIVALS, rivalFitCount())))
      if (prev == null) return def()
      // Keep valid selections across data refreshes; fall back to the default if
      // nothing valid survives (e.g. a location switch replaced every rival).
      const valid = new Set([...prev].filter((n) => competitorSet.has(n)))
      return valid.size ? valid : def()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitorKey])

  const visibleRivalSet = matrixRivalSel ?? new Set(competitors)
  const matrixRivals = competitors.filter((c) => visibleRivalSet.has(c))
  // Adding or removing columns while scrolled right leaves the reader stranded
  // mid-set, looking at rivals they never scrolled to — Chrome's scroll
  // anchoring holds position as the table grows. Reset after the new columns
  // have laid out, not in the change handler (which runs before they exist).
  const mxRivalCount = matrixRivals.length
  useLayoutEffect(() => {
    const el = matrixScrollRef.current
    if (el) el.scrollLeft = 0
  }, [mxRivalCount])
  // Seed the horizontal-travel measurement without waiting for a first scroll —
  // otherwise the affordance the fade exists to provide is missing exactly when
  // it is needed, on the untouched screen.
  useLayoutEffect(() => {
    const el = matrixScrollRef.current
    if (!el) return
    const measure = () => mxMeasureRef.current()
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [view, matrixRivals.length, matrixWindow.length])
  const hiddenRivalCount = competitors.length - matrixRivals.length

  // Frozen-column geometry (Part C1). Product · Your price · Position = 424px.
  // Header is two name lines + the shared/lower line.
  const MX_HEAD_H = 58
  // Fades hug the table, not the viewport. Spanning the whole container drew a
  // gradient down the empty area under a short table, which reads as a stray
  // vertical rule rather than as content continuing off-screen.
  const mxFadeH = Math.min(matrixViewportH || 0, MX_HEAD_H + matrixOrdered.length * MATRIX_ROW) || undefined
  // Right edge of the frozen block: a hard line + a soft shadow so the fixed /
  // scrolling boundary is unmistakable. Applied to the rightmost frozen column.
  // Right edge of the frozen block. The soft half is deliberately wide: a rival
  // column scrolled halfway under the frozen cells gets its price sliced down
  // the middle ("OU −$3.00"), which reads as broken text rather than as content
  // passing behind a fixed layer. Fading the first ~14px past the edge makes the
  // clipping legible as depth instead of damage.
  const MX_FROZEN_EDGE = 'var(--frozen-edge)'

  // Row Position over the VISIBLE rivals that carry the SKU (Part C2 + round 2
  // §7). n counts only shown columns, so the label always reconciles with the
  // visible cells. Descriptive "Above X of N" / "Below X of N" / "At parity" /
  // "Mixed" wherever possible; `weak` flags a thin comparison (1–2 rivals) so
  // the cell shows a low-evidence indicator instead of a blanket "Limited
  // evidence" on every sparse row.
  function matrixPosition(product: string): { label: string; tone: PillTone } {
    const yp = productYourPrice.get(product) ?? null
    const compMap = productMap.get(product)
    const carrying = matrixRivals.filter((c) => compMap?.has(c))
    const n = carrying.length
    if (n === 0) return { label: 'Not carried', tone: 'neutral' }
    if (yp == null) return { label: "Don't carry", tone: 'neutral' }
    let above = 0, below = 0
    for (const c of carrying) {
      const gap = yp - compMap!.get(c)!.price
      if (gap > PARITY) above += 1
      else if (gap < -PARITY) below += 1
    }
    // Plain two-word verdicts, not "Higher than X of N". The per-rival deltas in
    // the cells already carry the count; the frozen column just needs the gist,
    // and the founder read the old count-labels as analyst noise (review
    // 2026-08-18). "Above/Below rivals" pairs with the red/teal cell colours.
    if (above === 0 && below === 0) return { label: 'At parity', tone: 'neutral' }
    if (above > below) return { label: 'Above rivals', tone: 'danger' }
    if (below > above) return { label: 'Below rivals', tone: 'accent' }
    return { label: 'Mixed', tone: 'warning' }
  }

  // ── Begin review controls ───────────────────────────────────────────────────
  const beginReview = () => {
    if (!queueRows.length) return
    setView('priority')
    setReviewIndex(0)
    setSelectedProduct(queueRows[0].product)
    setReviewMode(true)
  }
  const reviewCount = queueRows.length
  const reviewCurrent = reviewMode ? queueRows[Math.min(reviewIndex, Math.max(0, reviewCount - 1))] : null

  // ── Inspector data for the selected product ─────────────────────────────────
  const inspRow = selectedProduct != null && productMap.has(selectedProduct)
    ? queueRowFor(selectedProduct)
    : null

  // Grounded decision copy, generated from real numbers.
  function decisionFor(r: QueueRow): { label: string; sentence: string; color: string }[] {
    const out: { label: string; sentence: string; color: string }[] = []
    const yp = r.yourPrice
    if (r.reviewClass === 'volumeRisk' && yp != null) {
      const aboveAvg = yp - r.marketAvg
      const aboveLow = yp - r.rivalLow
      out.push({
        label: CLASS_STATUS_LABEL.volumeRisk,
        sentence: `Your price is ${money(aboveAvg)} above the market average and ${money(aboveLow)} above the lowest rival.`,
        color: CLASS_COLOR.volumeRisk,
      })
    } else if (r.reviewClass === 'marginOpportunity' && yp != null) {
      const belowAvg = r.marketAvg - yp
      out.push({
        label: CLASS_STATUS_LABEL.marginOpportunity,
        sentence: `Your price is ${money(belowAvg)} below the market average. Confirm whether the discount is intentional.`,
        color: CLASS_COLOR.marginOpportunity,
      })
    } else if (r.reviewClass === 'limitedEvidence') {
      out.push({
        label: CLASS_STATUS_LABEL.limitedEvidence,
        sentence: 'Only one reliable rival match is available.',
        color: CLASS_COLOR.limitedEvidence,
      })
    }
    // Promotion pressure overlay — when ≥50% of matched rivals are promoting.
    if (r.rivals > 0 && r.promoRivals / r.rivals >= 0.5) {
      out.push({
        label: 'Promotion pressure',
        sentence: `${r.promoRivals} of ${r.rivals} matched rivals are currently promoting this product.`,
        color: 'var(--warm)',
      })
    }
    return out
  }
  // Matrix cell legend. It explains the colour of a MATRIX cell, so sitting in
  // the topbar cost ~400px of the top band on every view — including the two
  // views that have no matrix at all. It now renders in the control band, and
  // only when the matrix is on screen.
  const matrixLegend = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', rowGap: 4 }}>
              {[
                { bg: 'rgba(196,59,78,0.25)', border: 'var(--danger)', label: "You're higher", color: 'var(--danger)' },
                { bg: 'rgba(11,184,184,0.22)', border: 'var(--accent)', label: "You're lower", color: 'var(--accent)' },
                { bg: 'rgba(84,132,164,0.12)', border: 'var(--slate)', label: 'At parity', color: 'var(--text-2)' },
                { bg: 'var(--surface-3)', border: 'var(--border-2)', label: 'Not carried', color: 'var(--text-3)' },
              ].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600, color: l.color }}>
                  <div style={{ width: 28, height: 14, borderRadius: 4, background: l.bg, border: `2px solid ${l.border}`, flexShrink: 0 }} />
                  {l.label}
                </div>
              ))}
    </div>
  )


  // The DEMOTED filters that are currently set, NAMED. A count answers "how
  // many"; it does not answer "which", and a filter the user cannot name is a
  // filter they cannot trust — the standing failure of every "more filters"
  // pattern. FilterBar renders these as removable chips beside the disclosure,
  // so reading and undoing collapsed state never costs a trip into the tray.
  const activeFilterChips: ActiveFilterChip[] = []
  if (view === 'byrival') {
    if (rivalMinShared > 0) {
      activeFilterChips.push({ key: 'minShared', label: `${rivalMinShared}+ shared SKUs`, onRemove: () => setRivalMinShared(0) })
    }
    if (rivalSort !== 'pressure') {
      activeFilterChips.push({ key: 'rivalSort', label: `Sorted: ${RIVAL_SORT_LABEL[rivalSort]}`, onRemove: () => setRivalSort('pressure') })
    }
  } else {
    if (category) {
      activeFilterChips.push({ key: 'category', label: category, onRemove: () => setCategory('') })
    }
    if (brandFilter) {
      activeFilterChips.push({
        key: 'brand',
        label: brandFilter === UNBRANDED ? UNBRANDED_LABEL : brandFilter,
        onRemove: () => setBrandFilter(''),
      })
    }
    if (promoFilter !== 'all') {
      activeFilterChips.push({ key: 'promo', label: promoFilter === 'promo' ? 'On promo' : 'No promo', onRemove: () => setPromoFilter('all') })
    }
    if (scopeShared) {
      activeFilterChips.push({ key: 'shared', label: 'Shared brands only', onRemove: () => setScopeShared(false) })
    }
    if (view === 'priority' && sortMode !== 'recommended') {
      activeFilterChips.push({ key: 'sort', label: `Sorted: ${QUEUE_SORT_LABEL[sortMode]}`, onRemove: () => setSortMode('recommended') })
    }
  }
  if (operatorType !== 'both') {
    activeFilterChips.push({ key: 'operator', label: operatorType === 'storefront' ? 'Storefront only' : 'Delivery only', onRemove: () => setOperatorType('both') })
  }

  const clearMoreFilters = () => {
    if (view === 'byrival') {
      setRivalMinShared(0)
      setRivalSort('pressure')
    } else {
      setCategory('')
      setBrandFilter('')
      setPromoFilter('all')
      setScopeShared(false)
      setSortMode('recommended')
    }
    setOperatorType('both')
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── PAGE HEADER (UI-002) ──
          Was a hand-rolled topbar carrying the title, coverage line, operator
          filter, a four-swatch matrix legend, Export CSV and the location
          select in a single row. Natural width 1,138–1,232px, so it wrapped at
          every width tested and the control cluster read as loudly as the page
          title. The legend moved to the control band below (matrix view only);
          the rest is the shared header, so this page now opens the same way
          every other screen does. */}
      <PageHeader
        paddingX={24}
        dense={isNarrow}
        title="Price Intelligence"
        subtitle={
          <span data-testid="prices-coverage">
            {totalSkus.toLocaleString()} SKUs · {competitors.length} rivals · Updated {lastUpdated}
          </span>
        }
        onBack={() => navigate('/locations')}
        backLabel={locationName}
        actions={
          <>
            <button
              className="btn"
              onClick={() => exportCSV(products, competitors, productMap, productYourPrice)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', fontSize: 11 }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export CSV
            </button>
            {locations.length > 1 && (
              <select
                value={selectedLocation || ''}
                onChange={e => setSelectedLocation(e.target.value)}
                style={{
                  background: 'var(--surface-3)', border: '1.5px solid var(--border-2)',
                  borderRadius: 'var(--r-sm)', padding: '7px 13px',
                  color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--sans)',
                  cursor: 'pointer',
                }}
              >
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </>
        }
      />

      {!loading && !error && view === 'priority' && topQueueRow && (
        <section className="price-decision-brief" data-testid="prices-decision-brief">
          <div className="price-decision-brief__content">
            <div className="price-decision-brief__eyebrow">Recommended first review</div>
            <div className="price-decision-brief__answer">
              Review {topQueueRow.product} first.
            </div>
            <div className="price-decision-brief__evidence">
              {topQueueRow.reviewClass === 'volumeRisk' && topQueueRow.gapDollar != null
                ? `Your ${money(topQueueRow.yourPrice!)} price is ${money(topQueueRow.gapDollar)} above the ${money(topQueueRow.marketAvg)} market average and ${money(topQueueRow.yourPrice! - topQueueRow.rivalLow)} above the lowest rival.`
                : topQueueRow.reviewClass === 'marginOpportunity' && topQueueRow.gapDollar != null
                  ? `Your ${money(topQueueRow.yourPrice!)} price is ${money(Math.abs(topQueueRow.gapDollar))} below the ${money(topQueueRow.marketAvg)} market average.`
                  : `Only ${topQueueRow.rivals} comparable rival${topQueueRow.rivals === 1 ? '' : 's'} currently support this pricing decision.`}
            </div>
            <div className="price-decision-brief__basis">
              {CLASS_STATUS_LABEL[topQueueRow.reviewClass]} · matched against {topQueueRow.rivals} rival{topQueueRow.rivals === 1 ? '' : 's'} · recommended queue order · updated {lastUpdated}
            </div>
          </div>
          <div className="price-decision-brief__action">
            <span>What to do next</span>
            <strong>
              {topQueueRow.reviewClass === 'volumeRisk'
                ? 'Check comparable rivals, then decide whether to adjust or defend the price.'
                : topQueueRow.reviewClass === 'marginOpportunity'
                  ? 'Evaluate a margin increase without leaving the competitive range.'
                  : 'Confirm more comparable prices before changing this SKU.'}
            </strong>
            <button type="button" onClick={beginReview}>Review this product →</button>
          </div>
        </section>
      )}

      {/* ── CONTROL BAND (UI-002) ──
          Eight controls of equal weight used to sit here in one row: view tabs,
          search, price position, category, brand, promotion, shared-only and
          sort — plus the operator scope up in the header. Below 1400 they wrap
          into two or three rows, so the page opened with a wall of chrome and
          the review queue started under it.

          The shared FilterBar ranks them instead of hiding them. Tabs, search
          and the one filter the current view is actually about stay visible;
          the rest fold behind a single disclosure that carries a count, so a
          collapsed filter is never a hidden filter. The matrix legend is
          trailing content and appears only where a matrix exists. */}
      <FilterBar
        data-testid="prices-filter-bar"
        paddingX={24}
        // Measured 2026-08-17 (e2e/top-density.spec.ts): with the band expanded
        // at 1440 it rendered 88px — TWO rows — against 48px collapsed at 1280.
        // The widest width the founder calls "looks fine" was carrying the
        // heaviest control wall in the app. Eight controls plus separators do
        // not fit one row at any width this product is used at, so the band
        // collapses unconditionally rather than at a threshold that only ever
        // fires on the narrow side.
        collapseAt="always"
        activeFilters={activeFilterChips}
        onClearMore={clearMoreFilters}
        paddingY={isNarrow ? 6 : 8}
        primary={
          <>
            {/* View tabs */}
            <div style={{
              display: 'inline-flex', alignItems: 'stretch',
              background: 'var(--surface)', border: '1.5px solid var(--border-2)',
              borderRadius: 'var(--r-sm)', padding: 2, gap: 2, flexShrink: 0,
            }}>
              {([
                { v: 'priority' as View, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>, label: 'Needs review' },
                { v: 'byrival' as View, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, label: 'By rival' },
                { v: 'matrix' as View, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>, label: 'Full matrix' },
              ] as const).map(tab => (
                <button
                  key={tab.v}
                  onClick={() => setView(tab.v)}
                  style={{
                    padding: '5px 13px', borderRadius: 'calc(var(--r-sm) - 2px)',
                    fontSize: 11, fontWeight: 600, fontFamily: 'var(--sans)',
                    cursor: 'pointer', whiteSpace: 'nowrap', border: 'none',
                    display: 'flex', alignItems: 'center', gap: 6, lineHeight: 1.2,
                    background: view === tab.v ? 'var(--accent)' : 'transparent',
                    color: view === tab.v ? '#fff' : 'var(--text-2)',
                    boxShadow: view === tab.v ? '0 1px 4px rgba(9,161,161,0.25)' : 'none',
                    transition: 'var(--transition)',
                  }}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
            {sep}
            {view === 'byrival' ? (
              <>
                  {/* Search — rival name */}
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" style={{ position: 'absolute', left: 9, pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                      data-testid="prices-rival-search"
                      value={rivalSearch}
                      onChange={e => setRivalSearch(e.target.value)}
                      placeholder="Search rivals"
                      style={{
                        width: 170, padding: '6px 10px 6px 26px', borderRadius: 'var(--r-sm)',
                        border: `1.5px solid ${rivalSearch ? 'var(--accent)' : 'var(--border-2)'}`,
                        background: 'var(--surface)', color: 'var(--text-1)',
                        fontSize: 11, fontFamily: 'var(--sans)', outline: 'none',
                      }}
                    />
                  </div>
                {sep}
                  <Dropdown
                    label="Position"
                    value={rivalPosition}
                    onChange={v => setRivalPosition(v as RivalPosition | 'all')}
                    options={[
                      { value: 'all', label: 'All rivals' },
                      { value: 'pressure', label: 'Pricing pressure', dot: 'var(--danger)' },
                      { value: 'aligned', label: 'Mostly aligned', dot: 'var(--slate)' },
                      { value: 'lower', label: 'You generally lower', dot: 'var(--accent)' },
                      { value: 'limited', label: 'Not enough data', dot: 'var(--text-3)' },
                    ]}
                    active={rivalPosition !== 'all'}
                  />
              </>
            ) : (
              <>
                  {/* Search — product name + brand */}
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" style={{ position: 'absolute', left: 9, pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                      data-testid="prices-search"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search products, brands"
                      style={{
                        width: 190, padding: '6px 10px 6px 26px', borderRadius: 'var(--r-sm)',
                        border: `1.5px solid ${search ? 'var(--accent)' : 'var(--border-2)'}`,
                        background: 'var(--surface)', color: 'var(--text-1)',
                        fontSize: 11, fontFamily: 'var(--sans)', outline: 'none',
                      }}
                    />
                  </div>
                {sep}
                  <Dropdown
                    label="Price position"
                    value={pricePosition}
                    onChange={v => setPricePosition(v as PricePosition)}
                    options={[
                      { value: 'all', label: 'All positions' },
                      { value: 'above', label: 'Above market', dot: 'var(--danger)' },
                      { value: 'below', label: 'Below market', dot: 'var(--warm)' },
                      { value: 'at', label: 'At market', dot: 'var(--positive)' },
                      { value: 'notcarried', label: 'Not carried', dot: 'var(--text-3)' },
                    ]}
                    active={pricePosition !== 'all'}
                  />
              </>
            )}
          </>
        }
        more={
          view === 'byrival' ? (
            <>
                <Dropdown
                  label="Min shared"
                  value={String(rivalMinShared)}
                  onChange={v => setRivalMinShared(Number(v))}
                  options={[
                    { value: '0', label: 'Any' },
                    { value: '5', label: '5+' },
                    { value: '10', label: '10+' },
                    { value: '25', label: '25+' },
                    { value: '50', label: '50+' },
                  ]}
                  active={rivalMinShared > 0}
                />
              {sep}
                <Dropdown
                  label="Sort"
                  value={rivalSort}
                  onChange={v => setRivalSort(v as RivalSortMode)}
                  options={[
                    { value: 'pressure', label: 'Most pricing pressure' },
                    { value: 'shared', label: 'Most shared SKUs' },
                    { value: 'avgDiff', label: 'Largest average difference' },
                    { value: 'maxDiff', label: 'Largest single difference' },
                    { value: 'higher', label: "Most products where you're higher" },
                    { value: 'name', label: 'Rival name' },
                  ]}
                  active={rivalSort !== 'pressure'}
                />
              {sep}
              <OperatorTypeFilter value={operatorType} onChange={setOperatorType} />
            </>
          ) : (
            <>
                <Dropdown
                  label="Category"
                  value={category}
                  onChange={setCategory}
                  options={[
                    { value: '', label: 'All categories' },
                    ...categoryOptions.map((c) => ({ value: c, label: c })),
                  ]}
                  active={!!category}
                />
              {sep}
                <Dropdown
                  label="Brand"
                  value={brandFilter}
                  onChange={setBrandFilter}
                  options={[
                    { value: '', label: 'All brands' },
                    ...(hasUnbranded ? [{ value: UNBRANDED, label: UNBRANDED_LABEL, dot: 'var(--text-3)' }] : []),
                    ...brandOptions.map(b => ({
                      value: b.value,
                      label: `${b.label} · ${b.rivals}`,
                      dot: sharedBrandSet.has(b.value) ? 'var(--accent)' : undefined,
                    })),
                  ]}
                  active={!!brandFilter}
                />
              {sep}
                <Dropdown
                  label="Promotion"
                  value={promoFilter}
                  onChange={v => setPromoFilter(v as PromoFilter)}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'promo', label: 'On promo', dot: 'var(--warm)' },
                    { value: 'nopromo', label: 'No promo', dot: 'var(--slate)' },
                  ]}
                  active={promoFilter !== 'all'}
                />
              {sep}
                <button
                  onClick={() => setScopeShared(s => !s)}
                  title="Show only brands carried by 2 or more rivals"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '5px 11px', borderRadius: 'var(--r-sm)',
                    fontSize: 11, fontWeight: 600, fontFamily: 'var(--sans)',
                    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    border: `1.5px solid ${scopeShared ? 'var(--accent)' : 'var(--border-2)'}`,
                    background: scopeShared ? 'var(--accent)' : 'var(--surface)',
                    color: scopeShared ? '#fff' : 'var(--text-1)',
                    transition: 'var(--transition)',
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: scopeShared ? '#fff' : 'var(--accent)',
                  }} />
                  Shared only
                </button>
              {/* Queue sort has no meaning outside the review queue. */}
              {view === 'priority' && (
                <>
                  {sep}
                      <Dropdown
                        label="Sort"
                        value={sortMode}
                        onChange={v => setSortMode(v as SortMode)}
                        title="Ranked by urgency, evidence quality, and actionable price difference"
                        options={[
                          { value: 'recommended', label: 'Recommended order' },
                          { value: 'gap', label: 'Largest dollar gap' },
                          { value: 'pct', label: 'Largest percentage gap' },
                          { value: 'rivals', label: 'Most matched rivals' },
                          { value: 'promo', label: 'Promotion pressure' },
                          { value: 'brand', label: 'Brand' },
                        ]}
                        active={sortMode !== 'recommended'}
                      />
                </>
              )}
              {sep}
              <OperatorTypeFilter value={operatorType} onChange={setOperatorType} />
            </>
          )
        }
        // The matrix legend used to ride here AND in the matrix toolbar — the
        // same key twice, in two different wordings. The toolbar copy sits
        // beside the grid it explains, so this one is gone.
        trailing={undefined}
      />

      {/* ── CONTENT AREA ── */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 13 }}>
          Pulling latest prices from {competitors.length > 0 ? competitors.length : 'multiple'} sources…
        </div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--danger)', fontFamily: 'var(--mono)', fontSize: 13 }}>
          Couldn't load price data. We'll retry shortly.
        </div>
      ) : !selectedLocation ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 14 }}>
          Add a location to view price data.
        </div>
      ) : products.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'var(--text-3)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            No price data yet
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 280, textAlign: 'center' }}>
            Add rivals on this location and data will appear after the next collection window.
          </div>
        </div>
      ) : (
        <>
          {/* ── NEEDS REVIEW VIEW ── */}
          {view === 'priority' && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

              {/* ── INTELLIGENCE RIBBON — one compact status row (count · chips · Begin review · catalog) ── */}
              <style>{RIBBON_CSS}</style>
              <div data-testid="review-ribbon" className="ribbon">
                <div className="ribbon-main">
                  {/* Queue count block. When a class chip is active the table is
                      narrowed but the chip counts are not — so say "Showing N of M"
                      + Clear rather than showing two numbers that appear to disagree. */}
                  {classFilter ? (
                    <div className="ribbon-count is-filtered" data-testid="ribbon-count">
                      <span className="ribbon-count-label">Showing</span>
                      <span className="ribbon-count-num tnum" data-testid="queue-filtered-count">{displayQueueProducts.length.toLocaleString()}</span>
                      <span className="ribbon-count-label">of</span>
                      <span className="ribbon-count-num tnum" data-testid="queue-count">{counts.queue.toLocaleString()}</span>
                      <button className="ribbon-clear" data-testid="ribbon-clear" onClick={() => setClassFilter(null)}>Clear</button>
                    </div>
                  ) : (
                    <div className="ribbon-count" data-testid="ribbon-count">
                      <span className="ribbon-count-num tnum" data-testid="queue-count">{counts.queue.toLocaleString()}</span>
                      <span className="ribbon-count-label">Review queue</span>
                    </div>
                  )}

                  {/* Three class chips — full labels; the words carry the meaning. */}
                  <div className="ribbon-chips">
                    {([
                      { cls: 'volumeRisk' as ReviewClass, n: counts.volumeRisk, label: CLASS_STATUS_LABEL.volumeRisk, color: 'var(--danger)', tint: 'var(--danger-soft)' },
                      { cls: 'marginOpportunity' as ReviewClass, n: counts.marginOpportunity, label: CLASS_STATUS_LABEL.marginOpportunity, color: 'var(--warm)', tint: 'var(--warm-soft)' },
                      { cls: 'limitedEvidence' as ReviewClass, n: counts.limitedEvidence, label: CLASS_STATUS_LABEL.limitedEvidence, color: 'var(--text-3)', tint: 'var(--surface-3)' },
                    ]).map(c => {
                      const on = classFilter === c.cls
                      return (
                        <button
                          key={c.cls}
                          data-testid={`metric-${c.cls}`}
                          className={`ribbon-chip${on ? ' is-active' : ''}`}
                          onClick={() => setClassFilter(on ? null : c.cls)}
                          aria-pressed={on}
                          style={{ '--chip-color': c.color, '--chip-tint': c.tint } as CSSProperties}
                        >
                          <span className="ribbon-chip-num tnum">{c.n}</span>
                          <span className="ribbon-chip-label">{c.label}</span>
                        </button>
                      )
                    })}
                  </div>

                  {/* Begin review — next to the numbers it refers to. */}
                  <button
                    className="btn btn-primary ribbon-begin"
                    data-testid="begin-review"
                    onClick={beginReview}
                    disabled={counts.queue === 0}
                    style={{ padding: '6px 14px', opacity: counts.queue === 0 ? 0.5 : 1, cursor: counts.queue === 0 ? 'not-allowed' : 'pointer' }}
                  >
                    Begin review
                  </button>
                </div>

                {/* Handled context + catalog context — the right edge. */}
                <div className="ribbon-catalog" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {/* Decisions are never hidden silently: if anything has been
                      ticked, snoozed or flagged, say so and offer it back. */}
                  {(handledInScope > 0 || showHandled) && (
                    <button
                      className="ribbon-catlink"
                      data-testid="handled-toggle"
                      onClick={() => setShowHandled(v => !v)}
                      style={{ color: 'var(--text-2)' }}
                    >
                      <span className="tnum" data-testid="handled-count">{handledInScope.toLocaleString()}</span>
                      {' '}handled · {showHandled ? 'Hide' : 'Show'}
                    </button>
                  )}
                  {/* The long form is a sentence; below 1200 it is the single
                      reason the ribbon wraps to a second row. Same target, same
                      number, fewer words. */}
                  <button className="ribbon-catlink" data-testid="catalog-gap-link" onClick={() => navigate('/market/sku-gaps')}>
                    <span className="tnum" data-testid="catalog-gap-count">{counts.catalogGap.toLocaleString()}</span>
                    {isNarrow ? ' SKU gaps →' : ' market-only SKUs · View gap analysis →'}
                  </button>
                </div>
              </div>

              {/* Focus banner (Begin review) */}
              {reviewMode && reviewCurrent && (
                <div data-testid="review-focus" style={{
                  margin: '0 24px 10px', padding: '10px 14px', flexShrink: 0,
                  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                  background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 'var(--r-sm)',
                }}>
                  <span data-testid="review-progress" style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>
                    Product {Math.min(reviewIndex, reviewCount - 1) + 1} of {reviewCount}
                  </span>
                  <Pill variant="chip" tone={CLASS_PILL_TONE[reviewCurrent.reviewClass]}>{CLASS_STATUS_LABEL[reviewCurrent.reviewClass]}</Pill>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>{reviewCurrent.product}</span>
                  {reviewCurrent.gapDollar != null && (
                    <span className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, color: CLASS_COLOR[reviewCurrent.reviewClass] }}>{signedMoney(reviewCurrent.gapDollar)}</span>
                  )}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 7 }}>
                    <button className="btn" data-testid="review-prev" onClick={() => setReviewIndex(i => Math.max(0, i - 1))} disabled={reviewIndex <= 0} style={{ opacity: reviewIndex <= 0 ? 0.5 : 1 }}>Previous</button>
                    <button
                      className="btn"
                      data-testid="review-next"
                      title="Tick this product off and move to the next one"
                      onClick={() => {
                        // Moving on IS the review — tick the product off as you
                        // pass it, so walking the queue actually empties it.
                        // The row stays visible (justHandled) with its tick, so
                        // the action is both seen and reversible.
                        // Mark, never toggle: pressing Next on an already
                        // ticked product must not silently un-tick it.
                        if (reviewCurrent && stateFor(reviewCurrent.product)?.state !== 'reviewed') {
                          void saveReviewState(reviewCurrent.product, 'reviewed')
                        }
                        setReviewIndex(i => Math.min(reviewCount - 1, i + 1))
                      }}
                      disabled={reviewIndex >= reviewCount - 1}
                      style={{ opacity: reviewIndex >= reviewCount - 1 ? 0.5 : 1 }}
                    >
                      Reviewed · Next
                    </button>
                    <button className="btn btn-primary" data-testid="review-back" onClick={() => setReviewMode(false)}>Back to queue</button>
                  </div>
                </div>
              )}


              {/* The table is the page now. The detail panel below is an
                  overlay that appears on selection — see the note on it. */}
              <div style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
                {/* Table column — full width at every breakpoint. */}
                <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '12px 24px 16px', overflowY: 'auto' }}>
                  <div
                    data-testid="price-table"
                    data-cols="dense"
                    style={{
                      flexShrink: 0, overflowX: 'auto',
                      background: 'var(--surface-solid)',
                      border: '1px solid var(--border-2)',
                      borderRadius: 'var(--r-sm)',
                    }}
                  >
                    <DataTable
                      key={`nr-${sortMode}-${classFilter ?? 'all'}`}
                      rows={queueShown}
                      rowKey={(r) => r.product}
                      rowTestId="review-row"
                      density="comfortable"
                      columns={queueColumns}
                      empty="No products need review in this scope."
                      onRowClick={(r) => {
                        setSelectedProduct(r.product)
                        if (reviewMode) {
                          const idx = queueRows.findIndex(x => x.product === r.product)
                          if (idx >= 0) setReviewIndex(idx)
                        }
                      }}
                    />
                  </div>
                  <ShowMore
                    testid="queue-show-more"
                    shown={queueShown.length} total={queueRows.length} page={QUEUE_PAGE}
                    noun="in queue"
                    onMore={() => setQueueVisible(n => n + QUEUE_PAGE)}
                    onAll={() => setQueueVisible(queueRows.length)}
                  />
                </div>

                {/* Detail panel — opens ON SELECTION, over the table's right
                    edge, instead of holding 350px of permanent width.

                    Founder review 2026-08-18: the docked panel "competes with
                    and covers the table when the window changes size", and the
                    row actions it duplicated were "interfering with the table".
                    Both were the same mistake — a workflow surface charging the
                    table rent whether or not anyone was using it. Selecting a
                    row opens this; Esc or the close button dismisses it. The
                    table keeps its full width the rest of the time. */}
                {inspRow && (
                  <div
                    data-testid="sku-inspector"
                    role="dialog"
                    aria-label={`Detail for ${inspRow.product}`}
                    style={{
                      position: 'absolute', top: 0, right: 0, bottom: 0,
                      width: 'min(420px, 94%)', zIndex: 24,
                      borderLeft: '1px solid var(--border)',
                      background: 'var(--surface-solid)',
                      boxShadow: '-14px 0 30px -20px rgba(15,30,45,0.40)',
                      overflowY: 'auto', padding: '14px 16px', minHeight: 0,
                    }}
                  >
                    <>
                      <button
                        type="button"
                        data-testid="inspector-close"
                        aria-label="Close detail"
                        onClick={() => setSelectedProduct(null)}
                        style={{
                          float: 'right', width: 26, height: 26, borderRadius: 7,
                          border: '1px solid var(--border-2)', background: 'var(--surface)',
                          color: 'var(--text-2)', cursor: 'pointer', lineHeight: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}>
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                      {/* Selected SKU */}
                      <SectionLabel style={{ letterSpacing: '0.14em' }}>Selected SKU</SectionLabel>
                      <div data-testid="inspector-product" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', lineHeight: 1.3, marginTop: 6 }}>{inspRow.product}</div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 4, marginBottom: 12 }}>
                        {(inspRow.brand || UNBRANDED_LABEL)} · {inspRow.category || 'Uncategorised'} · matched against {inspRow.rivals} rival{inspRow.rivals === 1 ? '' : 's'}
                      </div>

                      {/* Decision block(s) */}
                      {decisionFor(inspRow).map((d, i) => (
                        <div key={i} style={{ marginBottom: 10, padding: '10px 12px', background: 'var(--surface-3)', border: '1px solid var(--border)', borderLeft: `3px solid ${d.color}`, borderRadius: 'var(--r-sm)' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: d.color, marginBottom: 3 }}>{d.label}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.45 }}>{d.sentence}</div>
                        </div>
                      ))}

                      {/* Four stats */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, margin: '12px 0' }}>
                        <StatTile size="sm" value={inspRow.yourPrice != null ? money(inspRow.yourPrice) : '—'} label="Your price" />
                        <StatTile size="sm" value={money(inspRow.marketAvg)} label="Market average" />
                        <StatTile size="sm" value={money(inspRow.rivalLow)} label="Lowest rival" />
                        <StatTile size="sm" value={inspRow.rivals} label="Matched rivals" />
                      </div>

                      {/* The "Volume risk by category" count-bar block was removed here
                          (final /prices corrective pass): it was a queue-WIDE aggregate
                          — its counts summed to counts.volumeRisk, the same number the
                          review ribbon's class chip already shows — rendered inside a
                          panel headed "Selected SKU". It restated the ribbon in a second
                          format, described nothing about the selected product, and pushed
                          the actual per-SKU actions (Assign / Snooze / Not the same
                          product) below the fold. The inspector is now a single per-SKU
                          decision surface: decision block(s) → four stats → market context
                          → actions. Queue-level breakdowns live in the ribbon only. */}

                      {/* Collapsible market context + price history */}
                      <button
                        onClick={() => setHistoryOpen(o => !o)}
                        data-testid="inspector-history-toggle"
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
                          padding: '8px 11px', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)',
                        }}
                      >
                        Market context and price history
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: historyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}><polyline points="6 9 12 15 18 9" /></svg>
                      </button>
                      {historyOpen && (
                        <div style={{ marginTop: 12 }}>
                          <PriceHistoryStrip locationId={selectedLocation} productName={inspRow.product} />
                        </div>
                      )}
                      {/* The three row actions used to sit on EVERY row,
                          costing 233px of permanent table width to repeat the
                          same three buttons a few hundred times. They belong to
                          the product you are deciding about, so they live with
                          it. */}
                      {(() => {
                        const rs = stateFor(inspRow.product)
                        const st = rs?.state
                        const busy = savingKey === reviewKey(inspRow.product)
                        const openTasks = Number(rs?.open_task_count ?? 0) || 0
                        return (
                          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            <RowAction
                              testid="row-assign" tone="accent" disabled={busy} active={openTasks > 0}
                              title={openTasks > 0
                                ? `${openTasks} open task${openTasks === 1 ? '' : 's'} on this product — click to add another`
                                : 'Create a task for this product and hand it to someone'}
                              onClick={() => { setTaskSuggestions(null); setTaskTarget(inspRow) }}
                            >
                              {openTasks > 0 ? `Assigned · ${openTasks}` : 'Assign'}
                            </RowAction>
                            <RowAction
                              testid="row-snooze" tone="warm" disabled={busy} active={st === 'snoozed'}
                              title={`Hide this for ${SNOOZE_DAYS} days, then bring it back`}
                              onClick={() => toggleSnooze(inspRow.product)}
                            >
                              {st === 'snoozed' ? 'Snoozed' : 'Snooze'}
                            </RowAction>
                            <RowAction
                              testid="row-notcomparable" tone="rose" disabled={busy} active={st === 'not_comparable'}
                              title="Stop comparing this product against rivals"
                              onClick={() => toggleNotComparable(inspRow.product)}
                            >
                              Not the same product
                            </RowAction>
                            </div>
                            {/* Was "Wrong match", which failed the one-sentence
                                test in founder review — nobody could say what it
                                meant on sight. The function is worth keeping
                                (excluding false matches is real data hygiene), so
                                it now says what it does, with the explanation
                                visible instead of hidden in a tooltip. */}
                            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
                              “Not the same product” drops this SKU out of the comparison — use it when we
                              have matched your product to something that isn’t really the same item.
                            </div>
                          </div>
                        )
                      })()}
                    </>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── BY RIVAL VIEW ── */}
          {view === 'byrival' && (
            <div data-testid="byrival-table" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 24px 24px', display: 'flex', flexDirection: 'column' }}>
              <SectionLabel style={{ marginBottom: 6, flexShrink: 0 }}>Pricing comparisons</SectionLabel>
              {/* Rows arrive pre-sorted by the toolbar Sort control; the key
                  remounts DataTable when that changes so its internal (column-
                  header) sort resets and the toolbar order re-asserts. Clicking a
                  column header still takes over within the current sort. Only
                  rivals with shared SKUs live here (round 2 §4). */}
              {/* One scroll column: the table sizes to its rows and the
                  zero-comparable group follows immediately after it. Virtualizing
                  a max-20-row table bought nothing and forced a flex:1 wrapper,
                  which parked the group at the bottom of the viewport — hundreds
                  of pixels below the list it belongs to, reading as a page
                  footer rather than the tail of the list. */}
              <div style={{
                flexShrink: 0,
                background: 'var(--surface-solid)',
                border: '1px solid var(--border-2)',
                borderRadius: 'var(--r-sm)',
                overflow: 'hidden',
              }}>
                <DataTable
                  key={`br-${rivalSort}`}
                  rows={byRivalShown}
                  rowKey={(r) => r.competitor}
                  rowTestId="byrival-row"
                  density="comfortable"
                  edgeGutter={16}
                  columns={byRivalColumns}
                  empty="No rivals with comparable SKUs match these filters."
                  onRowClick={(r) => openRivalDrawer(r)}
                />
                <ShowMore
                  card
                  testid="byrival-show-more"
                  shown={byRivalShown.length} total={byRivalMain.length} page={RIVAL_PAGE}
                  noun="rivals"
                  onMore={() => setRivalVisible(n => n + RIVAL_PAGE)}
                  onAll={() => setRivalVisible(byRivalMain.length)}
                />

              {/* Zero-comparable rivals — demoted, keyboard-expandable (round 2 §4). */}
              {byRivalZero.length > 0 && (
                <div style={{ flexShrink: 0, borderTop: '1px solid var(--border)', padding: '10px 0 12px', marginTop: 20 }}>
                  <button
                    type="button"
                    data-testid="zero-comparable-toggle"
                    aria-expanded={zeroCompOpen}
                    onClick={() => setZeroCompOpen((o) => !o)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
                      fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, color: 'var(--text-2)',
                    }}
                  >
                    <span className="tnum" style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--text-1)' }}>{byRivalZero.length}</span>
                    {byRivalZero.length === 1 ? 'rival has' : 'rivals have'} no comparable SKUs
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: zeroCompOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}><polyline points="6 9 12 15 18 9" /></svg>
                  </button>
                  {zeroCompOpen && (
                    <div data-testid="zero-comparable-list" style={{ maxHeight: 200, overflowY: 'auto', marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {byRivalZero.map((r) => (
                        <button
                          key={r.competitor}
                          type="button"
                          data-testid="zero-comparable-row"
                          onClick={() => openRivalDrawer(r)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                            background: 'none', border: 'none', padding: '5px 6px', borderRadius: 'var(--r-sm)',
                            cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--text-2)',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-3)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-3)', flexShrink: 0 }} />
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-1)' }} title={r.competitor}>{r.competitor}</span>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }}>{operatorTypeLabel(r.businessType)}</span>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }}>0 shared SKUs</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>
          )}

          {/* ── FULL MATRIX VIEW ── */}
          {view === 'matrix' && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

              {/* Matrix toolbar area — comparable toggle + honest count + rivals
                  selector + hidden note, and the compact price-position legend. */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '9px 24px', flexShrink: 0, flexWrap: 'wrap', rowGap: 8,
                borderBottom: '1px solid var(--border)', background: 'var(--surface-2)',
              }}>
                <button
                  data-testid="matrix-comparable-toggle"
                  role="switch"
                  aria-checked={matrixComparable}
                  onClick={() => setMatrixComparable(v => !v)}
                  title={matrixComparable
                    ? 'Showing only SKUs you carry that a rival also prices. Click to include every SKU.'
                    : 'Show only SKUs you carry that a rival also prices.'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '4px 10px', borderRadius: 'var(--r-sm)',
                    fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    border: `1.5px solid ${matrixComparable ? 'var(--accent)' : 'var(--border-2)'}`,
                    background: matrixComparable ? 'var(--accent)' : 'var(--surface)',
                    color: matrixComparable ? '#fff' : 'var(--text-1)',
                    transition: 'var(--transition)',
                  }}
                >
                  <span style={{
                    width: 14, height: 14, borderRadius: 4, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1.5px solid ${matrixComparable ? '#fff' : 'var(--border-2)'}`,
                    background: matrixComparable ? 'transparent' : 'transparent',
                  }}>
                    {matrixComparable && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><polyline points="20 6 9 17 4 12" /></svg>}
                  </span>
                  Comparable SKUs only
                </button>
                {/* Round 2 §10: "3 of 1,943 SKUs" was ambiguous. Two lines make
                    the comparable set explicit and preserve the honesty count. */}
                <span style={{ display: 'inline-flex', flexDirection: 'column', fontFamily: 'var(--mono)', fontSize: 10.5, lineHeight: 1.25, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                  <span style={{ color: 'var(--text-1)', fontWeight: 700 }}>{mTotal.toLocaleString()} comparable products</span>
                  <span>of {totalSkus.toLocaleString()} market SKUs</span>
                </span>

                <MatrixRivalsMenu
                  rivals={competitors.map(c => ({ name: c, shared: rivalStatByName.get(c)?.shared ?? 0 }))}
                  selected={visibleRivalSet}
                  total={competitors.length}
                  onChange={setMatrixRivalSel}
                  top5={topRivalNames}
                />
                {hiddenRivalCount > 0 && (
                  <span data-testid="matrix-hidden-note" style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--text-3)' }}>
                    <span className="tnum" style={{ color: 'var(--warm)', fontWeight: 700 }}>{hiddenRivalCount}</span> hidden · use Rivals ▾ to show
                  </span>
                )}

                {/* Compact price-position legend (Part C8) */}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  {[
                    { c: 'var(--danger)', l: 'Higher than rival' },
                    { c: 'var(--accent)', l: 'Lower than rival' },
                    { c: 'var(--slate)', l: 'Parity' },
                    { c: 'transparent', l: 'Not carried', border: 'var(--border-2)' },
                    { c: 'var(--warm)', l: 'Sale', bar: true },
                  ].map(item => (
                    <span key={item.l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                      <span style={{ width: item.bar ? 3 : 9, height: item.bar ? 12 : 9, borderRadius: item.bar ? 1 : 2, background: item.c, border: item.border ? `1px solid ${item.border}` : 'none', flexShrink: 0 }} />
                      {item.l}
                    </span>
                  ))}
                </div>
              </div>

              {/* Fits its rows, then stops. Filling the viewport left ~220px of
                  blank white under a 15-row board — the same "empty" reading the
                  other two views had. */}
              <div style={{
                flex: '0 1 auto', minHeight: 0, position: 'relative',
                display: 'flex',
                background: 'var(--surface-solid)',
                border: '1px solid var(--border-2)',
                borderRadius: 'var(--r-sm)',
                overflow: 'hidden',
                margin: '0 24px 16px',
              }}>
              <div
                ref={matrixScrollRef}
                data-testid="matrix-scroll"
                tabIndex={0}
                onScroll={e => {
                  const el = e.currentTarget
                  setMatrixScrollTop(el.scrollTop)
                  setMxScrollX({ left: el.scrollLeft, max: el.scrollWidth - el.clientWidth })
                }}
                onKeyDown={matrixKeyDown}
                style={{
                  position: 'relative', width: '100%', maxHeight: '100%', overflow: 'auto', outline: 'none',
                  // Snap horizontal scrolling to rival-column boundaries, offset by
                  // the frozen block. A free scroll leaves a column sliced down the
                  // middle behind the frozen cells — you get "OU −$3.00" and a
                  // half-word of a rival name, which reads as broken rendering.
                  // Snapping means a rival column is either fully past the frozen
                  // edge or fully behind it. `proximity` (not `mandatory`) so a
                  // deliberate drag still moves freely and only settles on release.
                  scrollSnapType: 'x proximity',
                  scrollPaddingLeft: MX_PRODUCT_W + MX_PRICE_W + MX_POS_W,
                }}
              >
                {/* One restrained row-hover tint, spanning the frozen + scrolling
                    cells together (Part C7). The per-column hover highlight was
                    removed: flashing a whole column on every mouse move read as
                    fussy chatter and helped no decision (founder review). */}
                <style>{`.mx-row td{transition:filter .12s}.mx-row:hover td{filter:brightness(0.97)}`}</style>
                {/* Explicit width = exact column sum. A width:auto fixed-layout
                    table stretches to the container and distributes the surplus
                    into the columns, which would push the frozen edge past 424. */}
                {/* The blank right-hand area (founder review 2026-08-18) was this: the
                    table took a FIXED pixel width — frozen block plus one column
                    per selected rival — so with five rivals selected it simply
                    stopped, and everything past it was empty page. It is now a
                    minimum rather than a target: the columns share any width the
                    container has going spare, and the horizontal scroll still
                    engages once the rivals genuinely outgrow the viewport. */}
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%', minWidth: MX_PRODUCT_W + MX_PRICE_W + MX_POS_W + matrixRivals.length * MX_RIVAL_W }}>
                  <thead>
                    <tr>
                      <th style={{
                        position: 'sticky', top: 0, left: 0, zIndex: 8,
                        width: MX_PRODUCT_W, minWidth: MX_PRODUCT_W, height: MX_HEAD_H,
                        background: 'var(--surface-2-solid)', textAlign: 'left', verticalAlign: 'bottom',
                        borderBottom: '2px solid var(--border-2)', borderRight: '1px solid var(--border)',
                        padding: '0 14px 8px',
                      }}>
                        <SectionLabel style={{ letterSpacing: '0.12em' }}>Product</SectionLabel>
                      </th>
                      <th style={{
                        position: 'sticky', top: 0, left: MX_PRODUCT_W, zIndex: 8,
                        width: MX_PRICE_W, minWidth: MX_PRICE_W, height: MX_HEAD_H,
                        background: 'var(--surface-2-solid)', textAlign: 'right', verticalAlign: 'bottom',
                        borderBottom: '2px solid var(--border-2)', borderRight: '1px solid var(--border)',
                        padding: '0 10px 8px',
                      }}>
                        <SectionLabel style={{ letterSpacing: '0.10em' }}>Your price</SectionLabel>
                      </th>
                      <th style={{
                        position: 'sticky', top: 0, left: MX_PRODUCT_W + MX_PRICE_W, zIndex: 8,
                        width: MX_POS_W, minWidth: MX_POS_W, height: MX_HEAD_H,
                        background: 'var(--surface-2-solid)', textAlign: 'left', verticalAlign: 'bottom',
                        borderBottom: '2px solid var(--border-2)',
                        padding: '0 10px 8px', boxShadow: MX_FROZEN_EDGE,
                      }}>
                        <SectionLabel style={{ letterSpacing: '0.10em' }}>Position</SectionLabel>
                      </th>
                      {matrixRivals.map((c, ci) => {
                        const rs = rivalStatByName.get(c)
                        // Just "N shared" under the rival name. The "· N lower"
                        // tail was a second technical stat on every column header —
                        // chatter the cells below already show. The full breakdown
                        // stays one hover away in the header title.
                        const metaLine = rs ? `${rs.shared} shared` : '—'
                        const metaFull = rs
                          ? `${c}: ${rs.shared} shared SKUs. The rival is priced lower than you on ${rs.higher}, higher on ${rs.lower}.`
                          : c
                        return (
                          <th key={c} data-testid="matrix-rival-header" data-colidx={ci} title={metaFull} style={{
                            position: 'sticky', top: 0, zIndex: 6,
                            scrollSnapAlign: 'start',
                            width: MX_RIVAL_W, minWidth: MX_RIVAL_W, height: MX_HEAD_H,
                            background: 'var(--surface-2-solid)', verticalAlign: 'bottom', textAlign: 'left',
                            borderBottom: '2px solid var(--border-2)', borderRight: '1px solid var(--border)',
                            padding: '6px 10px',
                          }}>
                            {/* Two lines before the ellipsis. One line turned
                                "Coastal Collective - Long Beach" into
                                "Coastal Collective - L…", which is not enough to
                                tell two rivals apart; two lines identifies the
                                rival without widening the column. */}
                            <div style={{
                              fontSize: 11.5, fontWeight: 700, color: 'var(--text-1)', lineHeight: 1.25,
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                              overflow: 'hidden', marginBottom: 2, wordBreak: 'break-word',
                            }}>{c}</div>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {metaLine}
                            </div>
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {mTopPad > 0 && (
                      <tr aria-hidden>
                        <td colSpan={matrixRivals.length + 3} style={{ height: mTopPad, padding: 0, border: 'none' }} />
                      </tr>
                    )}
                    {matrixWindow.map(product => {
                      const yp = productYourPrice.get(product) ?? null
                      const v = productVerdict(product)
                      const pos = matrixPosition(product)
                      const carrying = matrixRivals.filter(c => productMap.get(product)?.has(c))
                      const first = carrying.length ? { name: carrying[0], price: productMap.get(product)!.get(carrying[0])!.price } : null
                      return (
                        <tr key={product} className="mx-row" style={{ height: MATRIX_ROW }}>
                          {/* Frozen · Product */}
                          <td
                            onClick={() => first && openDrawer(product, first.name, first.price)}
                            style={{
                              position: 'sticky', left: 0, zIndex: 4,
                              width: MX_PRODUCT_W, minWidth: MX_PRODUCT_W, height: MATRIX_ROW,
                              background: 'var(--surface-solid)', cursor: first ? 'pointer' : 'default',
                              borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)',
                              padding: '0 14px',
                            }}
                          >
                            <span title={product} style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {product}
                            </span>
                          </td>
                          {/* Frozen · Your price */}
                          <td style={{
                            position: 'sticky', left: MX_PRODUCT_W, zIndex: 4,
                            width: MX_PRICE_W, minWidth: MX_PRICE_W, height: MATRIX_ROW,
                            background: 'var(--surface-solid)', textAlign: 'right',
                            borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)',
                            padding: '0 10px',
                          }}>
                            <span className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: yp != null ? VERDICT_TEXT[v] : 'var(--text-3)' }}>
                              {yp != null ? `$${yp.toFixed(2)}` : '—'}
                            </span>
                          </td>
                          {/* Frozen · Position */}
                          <td data-testid="matrix-position" style={{
                            position: 'sticky', left: MX_PRODUCT_W + MX_PRICE_W, zIndex: 4,
                            width: MX_POS_W, minWidth: MX_POS_W, height: MATRIX_ROW,
                            background: 'var(--surface-solid)',
                            borderBottom: '1px solid var(--border)',
                            padding: '0 10px', boxShadow: MX_FROZEN_EDGE,
                          }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                              <Pill variant="chip" tone={pos.tone}>{pos.label}</Pill>
                            </span>
                          </td>
                          {/* Rival cells — price primary, YOU delta secondary */}
                          {matrixRivals.map((c, ci) => {
                            const d = productMap.get(product)?.get(c)
                            if (!d) {
                              return (
                                <td key={c} data-colidx={ci} style={{
                                  width: MX_RIVAL_W, minWidth: MX_RIVAL_W, height: MATRIX_ROW,
                                  background: CELL_COLORS.missing,
                                  borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)',
                                  textAlign: 'right', padding: '0 10px',
                                }}>
                                  <span className="tnum" aria-label="Not carried" style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>—</span>
                                </td>
                              )
                            }
                            const gap = yp != null ? yp - d.price : null   // YOU perspective
                            const isParity = gap != null && Math.abs(gap) <= PARITY
                            return (
                              <td
                                key={c}
                                data-colidx={ci}
                                data-testid="matrix-rival-cell"
                                onClick={(e) => openCellPopover(e, product, c, d.price, d.onPromo)}
                                title={d.onPromo ? 'On sale' : undefined}
                                style={{
                                  width: MX_RIVAL_W, minWidth: MX_RIVAL_W, height: MATRIX_ROW,
                                  background: VERDICT_BG[d.verdict],
                                  borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)',
                                  // Sale used to be a 6px dot tucked in beside the
                                  // price, where it competed with the digits and
                                  // still read as a speck. A full-height amber edge
                                  // costs no horizontal room, lines up down the
                                  // column, and is unmistakable at a glance.
                                  ...(d.onPromo ? { boxShadow: 'inset 3px 0 0 var(--warm)' } : null),
                                  padding: '0 10px', cursor: 'pointer',
                                }}
                              >
                                {/* Two-tier cell, not two competing numbers. The
                                    PRICE is the fact and leads (bold, --text-1);
                                    the cell BACKGROUND already carries direction
                                    (red = you higher, teal = you lower), so the
                                    delta is demoted to a quiet grey magnitude hint
                                    in the same weight as body text. Colouring the
                                    delta too was double-encoding direction and was
                                    the main reason the board read as a diagnostic
                                    grid rather than a comparison. */}
                                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 6, lineHeight: 1 }}>
                                  <span style={{ fontFamily: 'var(--mono)', fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)' }}>${d.price.toFixed(2)}</span>
                                  {gap == null ? null : isParity ? (
                                    <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 500, color: 'var(--text-3)' }}>=</span>
                                  ) : (
                                    <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 500, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                                      {gap > 0 ? '+' : '−'}{Math.abs(gap).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                    {mBotPad > 0 && (
                      <tr aria-hidden>
                        <td colSpan={matrixRivals.length + 3} style={{ height: mBotPad, padding: 0, border: 'none' }} />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Horizontal-scroll affordance (round 3): a fade on whichever
                  side still has rival columns beyond the edge. The left one
                  starts at the frozen boundary — fading over Product/Your
                  price/Position would imply those scroll too. Both vanish at
                  their respective ends, so an absent fade means "nothing more
                  that way". Purely decorative: pointer-events none. */}
              {mxScrollX.left > 1 && (
                <div aria-hidden data-testid="matrix-fade-left" style={{
                  position: 'absolute', top: 0, height: mxFadeH,
                  left: MX_PRODUCT_W + MX_PRICE_W + MX_POS_W, width: 18, pointerEvents: 'none',
                  background: 'linear-gradient(to right, var(--mx-fade), transparent)',
                }} />
              )}
              {mxScrollX.max - mxScrollX.left > 1 && (
                <div aria-hidden data-testid="matrix-fade-right" style={{
                  position: 'absolute', top: 0, right: 0, height: mxFadeH, width: 36, pointerEvents: 'none',
                  background: 'linear-gradient(to left, var(--mx-fade), transparent)',
                }} />
              )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── MATRIX CELL POPOVER (round 2 §13) ── */}
      {cellPop && (
        <MatrixCellPopover
          data={cellPop}
          openTasks={Number(stateFor(cellPop.product)?.open_task_count ?? 0) || 0}
          onAssign={() => {
            const gap = cellPop.yourPrice == null ? null : cellPop.yourPrice - cellPop.theirPrice
            setTaskSuggestions(cellTasks(cellPop.product, cellPop.competitor, cellPop.yourPrice, cellPop.theirPrice, cellPop.onPromo))
            setTaskTarget({
              product: cellPop.product, brand: null, category: null,
              rivals: 1, yourPrice: cellPop.yourPrice,
              marketAvg: cellPop.theirPrice, rivalLow: cellPop.theirPrice,
              gapDollar: gap, gapPct: gap == null || cellPop.theirPrice === 0 ? null : (gap / cellPop.theirPrice) * 100,
              reviewClass: gap == null ? 'catalogGap' : gap > PARITY ? 'volumeRisk' : gap < -PARITY ? 'marginOpportunity' : 'competitive',
              promoActive: cellPop.onPromo, promoRivals: cellPop.onPromo ? 1 : 0,
              firstRival: { name: cellPop.competitor, price: cellPop.theirPrice },
            })
            // Hand the screen over to the dialog — a popover behind a modal that
            // dismisses on outside click is a race waiting to happen.
            setCellPop(null)
          }}
          onClose={() => setCellPop(null)}
          onViewRival={(id) => { setCellPop(null); navigate(`/competitors/${id}`) }}
          // The standalone Price History page does not accept a product via URL,
          // so "Price history" opens the in-page SKU drawer, which carries the
          // real per-product history strip. (See report — noted, not invented.)
          onViewHistory={() => { openDrawer(cellPop.product, cellPop.competitor, cellPop.theirPrice); setCellPop(null) }}
        />
      )}

      {/* ── DETAIL DRAWER (single SKU — Full matrix only) ── */}
      <DetailDrawer data={drawerData} open={drawerOpen} onClose={() => setDrawerOpen(false)} locationId={selectedLocation} />

      {drawerOpen && (
        <div
          data-testid="drawer-scrim"
          onClick={() => setDrawerOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 45, background: 'rgba(0,0,0,0.15)' }}
        />
      )}

      {/* ── RIVAL DRAWER (full shared-SKU list — By rival) ── */}
      <RivalDrawer
        data={rivalDrawerData}
        open={rivalDrawerOpen}
        onClose={() => setRivalDrawerOpen(false)}
        openTasksFor={(product) => Number(stateFor(product)?.open_task_count ?? 0) || 0}
        onAssign={(sku) => {
          const rival = rivalDrawerData?.competitor ?? ''
          setTaskSuggestions(rivalSkuTasks(sku.product, rival, sku.yourPrice, sku.theirPrice, sku.gap, sku.onPromo))
          // The dialog and submitTask key off product name, which is all the
          // review-state row needs — the rival is carried in the task wording.
          setTaskTarget({
            product: sku.product, brand: sku.brand, category: sku.category,
            rivals: 1, yourPrice: sku.yourPrice, marketAvg: sku.theirPrice, rivalLow: sku.theirPrice,
            gapDollar: sku.gap, gapPct: sku.theirPrice === 0 ? null : (sku.gap / sku.theirPrice) * 100,
            reviewClass: sku.gap > PARITY ? 'volumeRisk' : sku.gap < -PARITY ? 'marginOpportunity' : 'competitive',
            promoActive: sku.onPromo, promoRivals: sku.onPromo ? 1 : 0,
            firstRival: { name: rival, price: sku.theirPrice },
          })
        }}
      />

      {rivalDrawerOpen && (
        <div
          data-testid="drawer-scrim"
          onClick={() => setRivalDrawerOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 45, background: 'rgba(0,0,0,0.15)' }}
        />
      )}

      {/* ── ASSIGN A TASK (Needs review row action) ── */}
      {taskTarget && (
        <AssignTaskDialog
          row={taskTarget}
          suggestions={taskSuggestions ?? undefined}
          saving={taskSaving}
          error={taskError}
          locationId={selectedLocation}
          onCancel={() => { setTaskTarget(null); setTaskSuggestions(null); setTaskError(null) }}
          onSubmit={submitTask}
        />
      )}
    </div>
  )
}
